import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { Service, PaymentMethod, DealServiceFormData, DealPaymentInstallment, DealVariable } from '../../lib/types';
import { supabase } from '../../lib/supabase';
import { X, ShoppingCart, Settings, Calendar, ChevronLeft, ChevronRight, Package, Search, Pencil } from 'lucide-react';

// VIVA: prodotti magazzino — record minimo per la lista nello step 2
interface ProductItem {
  id: string;
  sku: string | null;
  name: string;
  regular_price: number | null;
  stock_quantity: number | null;
  product_class: string | null;
  main_image_url: string | null;
  unit_of_measure_1: string | null;
  // Annotati a mano sulla scheda articolo, non calcolati: null = non usati
  committed_quantity: number | null;
  available_quantity: number | null;
}
import { CommittedAvailable } from '../magazzino/ui/CommittedAvailable';
import { fetchProductAvailability, type ProductAvailability } from '../../lib/deals/stockAvailability';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCube, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { DealServiceEditor, type DealServiceEditorHandle } from './DealServiceEditor';
import { PaymentScheduleCalculator } from './PaymentScheduleCalculator';
import { DealVariablesSection } from './DealVariablesSection';
import { DealServiceCostPreview } from './DealServiceCostPreview';
import { DealServiceAddonsPicker } from './DealServiceAddonsPicker';
import { useServiceAddonsStore } from '../../lib/stores/serviceAddonsStore';
import { PanelGroup } from './service-panel/PanelGroup';
import { PropertyRow } from './service-panel/PropertyRow';
import { useWarehouseSettings } from '../../lib/hooks/useWarehouseSettings';
import { processingNoteOf, withProcessingNote, PROCESSING_NOTE_KEY } from '../../lib/deals/lineProcessingNotes';
import { packagingSplitOf, withPackagingSplit, describePackagingSplit, packagingMismatch } from '../../lib/deals/linePackaging';
import { PriceHeadline } from './service-panel/PriceHeadline';
import { FrequencyPicker } from './service-panel/FrequencyPicker';
import { RateScheduleInline } from './service-panel/RateScheduleInline';
import { FullPageSheet } from './service-panel/FullPageSheet';
import {
  BILLING_PERIOD_LABEL,
  BILLING_PERIOD_SUFFIX,
  fullPriceForPeriod,
  listPriceForPeriod,
  monthlyBaseOf,
  parseBillingPrices,
} from '../../lib/utils/billingPrices';
import { DEFAULT_UNIT_OF_MEASURE, buildUnitOptions, resolveProductUnit } from '../../lib/constants/units';
import { formatQuantity } from '../../lib/utils/quantityFormat';
import { VAT_RATE_OPTIONS } from '../../lib/hooks/useProductQuickForm';
import { useDebounce } from '../../lib/hooks/useDebounce';

// Il catalogo articoli puo' superare le migliaia di righe: si carica a pagine
// e si cerca sul server, mai filtrando un buffer locale.
const PRODUCTS_PAGE_SIZE = 50;

// Quantita' e prezzo unitario: punto o virgola come decimale. Con
// type="number" il browser in italiano dava "" appena si scriveva "1."
// e il campo tornava a 0. null = testo non ancora un numero (si aspetta).
const parseDecimalInput = (raw: string): number | null => {
  const t = raw.trim().replace(/,/g, '.');
  if (t === '' || t === '.' || !/^\d*\.?\d*$/.test(t)) return null;
  const v = parseFloat(t);
  return Number.isFinite(v) && v >= 0 ? v : null;
};
import { SearchableSelect } from '../ui/searchable-select';
import { useDealVariableStore } from '../../lib/stores/dealVariableStore';
import { useDealServiceStore } from '../../lib/stores/dealServiceStore';
import { useServiceVariableTemplateStore } from '../../lib/stores/serviceVariableTemplateStore';
import { useServiceCostTemplateStore } from '../../lib/stores/serviceCostTemplateStore';
import { useTheme } from '../../contexts/ThemeContext';
import { useApp } from '../AppProvider';
import { useOrgUnitOfMeasure } from '../../lib/hooks/useOrgUnitOfMeasure';

interface EnhancedDealServiceSelectorProps {
  dealId?: string;
  services: Service[];
  paymentMethods: PaymentMethod[];
  selectedServices: DealServiceFormData[];
  onChange: (services: DealServiceFormData[]) => void;
  translations: any;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  externalSelectedServiceIndex?: number | null;
  onExternalServiceIndexChange?: (index: number | null) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  onServiceSaved?: (serviceIndex: number) => void;
  clientName?: string;
  /** Data di accettazione del deal (signed_at). Usata come "giorno 0" stabile
   * per le rate di pagamento a GG su offerte già accettate (R05). */
  dealSignedAt?: string | null;
  /** Porta al punto in cui si sceglie il metodo di pagamento dell'offerta
   * (tab Fatturazione): le righe prodotto lo dichiarano ma non lo decidono. */
  onOpenBillingSettings?: () => void;
  /** «Applica l'IVA del cliente» attivo sull'offerta: aliquota imposta a tutte le righe, non modificabile qui */
  lockedVatRate?: number | null;
}

export function EnhancedDealServiceSelector({
  dealId,
  services,
  paymentMethods,
  selectedServices,
  onChange,
  translations: t,
  formData = {},
  onFormDataChange = () => {},
  externalSelectedServiceIndex = null,
  onExternalServiceIndexChange = () => {},
  onUnsavedChangesChange = () => {},
  onServiceSaved = () => {},
  clientName,
  dealSignedAt = null,
  onOpenBillingSettings,
  lockedVatRate = null
}: EnhancedDealServiceSelectorProps) {
  const { isDark } = useTheme();
  const { toast, confirm } = useApp();

  // IMPORTANTE: Non manteniamo più selectedServiceIndex internamente - usiamo solo l'indice esterno
  // Usando solo externalSelectedServiceIndex come unica fonte di verità
  const [editingService, setEditingService] = useState<DealServiceFormData | null>(null);
  const [originalEditingService, setOriginalEditingService] = useState<DealServiceFormData | null>(null);
  // Ref all'editor della descrizione: permette di committare sincronicamente il
  // testo ancora in debounce prima di leggere editingService per il salvataggio.
  const descEditorRef = useRef<DealServiceEditorHandle>(null);
  // Quale proprietà del pannello è aperta (frequenza, pagamento, IVA…). Una
  // sola per volta: sono righe, non un modulo da compilare.
  const [openProp, setOpenProp] = useState<string | null>(null);
  // Testo in digitazione di quantita'/prezzo unitario (vedi parseDecimalInput)
  const [numDraft, setNumDraft] = useState<{ field: 'qty' | 'unit'; text: string } | null>(null);
  // Note di lavorazione per riga prodotto (Impostazioni → Offerte)
  const { line_processing_notes_enabled: lineNotesEnabled, line_packaging_split_enabled: packagingSplitEnabled } = useWarehouseSettings();
  // Foglio a tutta pagina delle rate (quello della descrizione lo apre
  // direttamente DealServiceEditor, che possiede l'istanza TipTap).
  const [sheet, setSheet] = useState<'rates' | null>(null);
  // Il gruppo Descrizione è controllato: "apri in grande" deve poterlo aprire
  // prima di chiedere all'editor di andare a tutta pagina, altrimenti l'editor
  // non è ancora montato e il click non farebbe nulla.
  const [descOpen, setDescOpen] = useState(false);
  // Stesso motivo per le rate: scegliendo un metodo con rate configurabili il
  // gruppo si apre da sé, senza scavalcare il pannello con un drawer.
  const [ratesOpen, setRatesOpen] = useState(false);
  // Catalogo add-on già caricato dal picker: serve qui per sapere il prezzo del
  // periodo di ciascun add-on quando cambia la frequenza della riga.
  const catalogAddons = useServiceAddonsStore((s) => s.addons);
  const [serviceVariables, setServiceVariables] = useState<DealVariable[]>([]);
  const [activeTab, setActiveTab] = useState<'details' | 'variables'>('details');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [useNextMonth, setUseNextMonth] = useState(false);

  // Normalizza il payment_schedule di un payment_method accettando:
  //  - formato corretto `{installments: [{type,value,...}, ...]}`
  //  - formato legacy/malformato array piatto `[{label, percentage, offset_days}]` (seed legacy o malformato)
  //  - undefined/null → []
  // Evita crash su seed malformati o metodi creati prima del formato attuale.
  const extractMethodInstallments = (method: any): any[] => {
    if (!method?.payment_schedule) return [];
    const ps: any = method.payment_schedule;
    if (Array.isArray(ps)) {
      return ps.map((inst: any) => ({
        type: 'percentage',
        value: Number(inst.percentage ?? 0),
        amount: 0,
        daysAfterStart: Number(inst.offset_days ?? 0),
        description: inst.label || 'Saldo',
        isConfigurable: false,
        timeUnit: 'days',
      }));
    }
    if (Array.isArray(ps.installments)) return ps.installments;
    return [];
  };

  // Helper per verificare se il metodo di pagamento ha rate con giorno fisso
  const hasFixedDayInstallments = (paymentMethodId: string) => {
    const method = paymentMethods.find(m => m.id === paymentMethodId);
    return extractMethodInstallments(method).some(
      (inst: any) => inst.fixedDayOfMonth !== undefined
    );
  };

  // Helper: converte i valori relativi del template in una data esplicita.
  // Usa dealSignedAt come base per offerte accettate (R05), altrimenti oggi.
  const computeFixedDateFromTemplate = (inst: any): string => {
    const today = dealSignedAt ? new Date(dealSignedAt) : new Date();
    if (inst.fixedDayOfMonth) {
      const d = new Date(today);
      if (inst.nextMonth) d.setMonth(d.getMonth() + 1);
      const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      d.setDate(Math.min(inst.fixedDayOfMonth, lastDay));
      return d.toISOString().split('T')[0];
    }
    const d = new Date(today);
    d.setDate(d.getDate() + (inst.daysAfterStart || 0));
    return d.toISOString().split('T')[0];
  };

  // Refs for smooth scrolling
  const serviceListRef = useRef<HTMLDivElement>(null);
  // Container scrollabile del pannello editor (destra). Scrollato in cima
  // ogni volta che si aggiunge/seleziona un servizio cosi' l'utente vede
  // subito i campi (Prezzo, Pagamento, ...) e non rimane sul punto precedente.
  const editorScrollRef = useRef<HTMLDivElement>(null);
  // Categorie: ref al container scrollabile + state per mostrare/nascondere frecce
  const categoriesScrollRef = useRef<HTMLDivElement>(null);
  const [categoriesCanScrollLeft, setCategoriesCanScrollLeft] = useState(false);
  const [categoriesCanScrollRight, setCategoriesCanScrollRight] = useState(false);

  const updateCategoriesScrollFlags = () => {
    const el = categoriesScrollRef.current;
    if (!el) return;
    setCategoriesCanScrollLeft(el.scrollLeft > 4);
    setCategoriesCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scrollCategoriesBy = (delta: number) => {
    categoriesScrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };


  // Filter services by searchTerm and category
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [customServiceName, setCustomServiceName] = useState('');

  // Categorie di servizio dell'organizzazione: servono alle righe scritte a mano.
  // Le righe prese dal catalogo ereditano la categoria dal servizio, quelle libere
  // no: senza categoria il cliente resta fuori dalle liste per servizio acquistato.
  const [categorieServizio, setCategorieServizio] = useState<Array<{ id: string; name: string }>>([]);

  // Unita' di misura della org (tabella units_of_measure), per il select
  // sulle righe prodotto. Vuoto finche' non caricate: si ripiega sulla
  // lista canonica.
  const [unitaOrg, setUnitaOrg] = useState<string[]>([]);
  // Unità predefinita dell'org (null se non impostata → comportamento di sempre)
  const { orgDefault: orgDefaultUnit } = useOrgUnitOfMeasure();

  // VIVA: tab switcher Servizi / Prodotti magazzino + 'added' (elenco di
  // cio' che e' gia' nel deal, sostituisce il carrellino in alto).
  const [listMode, setListMode] = useState<'service' | 'product' | 'added'>('service');

  // Listini e varianti del prodotto attualmente in editing (per la sezione
  // Prodotto: l'utente sceglie da quale listino prendere il prezzo e
  // l'eventuale variante). Cache locale per evitare re-fetch.
  const [productPriceLists, setProductPriceLists] = useState<Array<{ id: string; name: string; is_default: boolean }>>([]);
  const [productPrices, setProductPrices] = useState<Array<{ price_list_id: string; regular_price: number | null; sale_price: number | null }>>([]);
  const [productVariations, setProductVariations] = useState<Array<{ id: string; sku: string | null; regular_price: number | null; sale_price: number | null; stock_quantity: number | null }>>([]);
  // Stati prodotti magazzino + organizationId DEVONO essere dichiarati PRIMA
  // dell'useEffect che li referenzia (TDZ: ReferenceError altrimenti).
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsLoadingMore, setProductsLoadingMore] = useState(false);
  const [productsTotal, setProductsTotal] = useState<number | null>(null);
  const [productsPage, setProductsPage] = useState(0);
  const [productsError, setProductsError] = useState<string | null>(null);
  // Di default il catalogo in offerta mostra solo articoli con giacenza > 0
  const [onlyInStock, setOnlyInStock] = useState(true);
  // Accumula ogni articolo mai caricato: la lista visibile e' paginata e cambia
  // a ogni ricerca, ma il nome nel tab "Aggiunti" deve restare risolvibile.
  const productsCacheRef = useRef<Map<string, ProductItem>>(new Map());
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const editingProductId = editingService?.line_type === 'product' ? editingService.product_id : null;
  useEffect(() => {
    if (!editingProductId || !organizationId) {
      setProductPrices([]);
      setProductVariations([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [pricesRes, varsRes, listsRes] = await Promise.all([
          supabase
            .from('product_price_list_prices')
            .select('price_list_id, regular_price, sale_price')
            .eq('product_id', editingProductId),
          supabase
            .from('product_variations')
            .select('id, sku, regular_price, sale_price, stock_quantity, enabled')
            .eq('product_id', editingProductId),
          supabase
            .from('price_lists')
            .select('id, name, is_default')
            .eq('organization_id', organizationId)
            .eq('is_active', true)
            .order('is_default', { ascending: false }),
        ]);
        if (cancelled) return;
        if (pricesRes.data) setProductPrices(pricesRes.data as any);
        if (varsRes.data) {
          setProductVariations((varsRes.data as any[]).filter(v => v.enabled !== false));
        }
        if (listsRes.data) setProductPriceLists(listsRes.data as any);
      } catch (err) {
        if (!cancelled) console.error('[EnhancedDealServiceSelector] load product details failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [editingProductId, organizationId]);

  // "Disponibile" della scheda articolo della riga aperta, letto fresco: il
  // catalogo e' paginato e l'articolo di un'offerta gia' salvata puo' non
  // esserci. Serve all'avviso sotto la quantita'.
  const [editingAvailability, setEditingAvailability] = useState<ProductAvailability | null>(null);
  useEffect(() => {
    setEditingAvailability(null);
    if (!editingProductId) return;
    let cancelled = false;
    fetchProductAvailability([editingProductId])
      .then(map => { if (!cancelled) setEditingAvailability(map.get(editingProductId) ?? null); })
      .catch(err => console.error('[EnhancedDealServiceSelector] load availability failed', err));
    return () => { cancelled = true; };
  }, [editingProductId]);

  // Popover di conferma rimozione: fumetto sotto il bottone "Rimuovi"
  // nell'header dell'editor servizio. Click fuori o Esc chiude.
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const removePopoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!removeConfirmOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setRemoveConfirmOpen(false);
    };
    const onClickOutside = (e: MouseEvent) => {
      if (removePopoverRef.current && !removePopoverRef.current.contains(e.target as Node)) {
        setRemoveConfirmOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClickOutside);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClickOutside);
    };
  }, [removeConfirmOpen]);
  // Chiudi il popover se l'utente cambia servizio
  useEffect(() => {
    setRemoveConfirmOpen(false);
  }, [externalSelectedServiceIndex]);

  // Per riga prodotto la tab "Variabili e costi" e' nascosta: se l'utente
  // era sulla tab variables e seleziona un prodotto, forza il ritorno a details.
  useEffect(() => {
    if (editingService?.line_type === 'product' && activeTab === 'variables') {
      setActiveTab('details');
    }
  }, [editingService?.line_type, activeTab]);

  // Recupera organization_id dell'utente corrente per filtrare i prodotti per tenant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (cancelled || !userData.user) return;
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();
        if (cancelled) return;
        if (profile?.organization_id) setOrganizationId(profile.organization_id);
      } catch (err) {
        if (!cancelled) console.error('[EnhancedDealServiceSelector] fetch organizationId failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Categorie di servizio della org, per il campo obbligatorio sulle righe libere.
  useEffect(() => {
    if (!organizationId) return;
    let annullato = false;
    (async () => {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name')
        .eq('organization_id', organizationId)
        .order('name');
      if (annullato) return;
      if (error) {
        console.warn('[EnhancedDealServiceSelector] categorie servizio non caricate', error);
        return;
      }
      setCategorieServizio((data ?? []) as Array<{ id: string; name: string }>);
    })();
    return () => { annullato = true; };
  }, [organizationId]);

  // Unita' di misura configurate dall'organizzazione: le stesse che la scheda
  // articolo propone per la UDM. Senza, l'offerta offrirebbe una lista fissa
  // scollegata da quella del magazzino.
  useEffect(() => {
    if (!organizationId) return;
    let annullato = false;
    (async () => {
      const { data, error } = await supabase
        .from('units_of_measure')
        .select('id, name')
        .eq('organization_id', organizationId)
        .order('name');
      if (annullato) return;
      if (error) {
        console.warn('[EnhancedDealServiceSelector] unita di misura non caricate', error);
        return;
      }
      setUnitaOrg(((data ?? []) as Array<{ name: string }>).map(u => u.name).filter(Boolean));
    })();
    return () => { annullato = true; };
  }, [organizationId]);

  // Ricerca articoli: il testo va al server, non a un Array.filter locale.
  const debouncedProductSearch = useDebounce(searchTerm, 300);

  // Ogni fetch prende un numero progressivo: quando l'utente digita in fretta
  // le risposte tornano fuori ordine, e solo l'ultima richiesta partita puo'
  // scrivere nello stato.
  const productsRequestRef = useRef(0);

  const caricaProdotti = React.useCallback(async (page: number, query: string, soloDisponibili: boolean) => {
    if (!organizationId) return;
    const requestId = ++productsRequestRef.current;
    if (page === 0) {
      setProductsLoading(true);
    } else {
      setProductsLoadingMore(true);
    }
    setProductsError(null);
    try {
      let q = supabase
        .from('products')
        .select(
          'id, sku, name, regular_price, stock_quantity, product_class, main_image_url, unit_of_measure_1, committed_quantity, available_quantity',
          { count: 'exact' }
        )
        .eq('organization_id', organizationId)
        .eq('status', 'active');

      // Filtro lato server: conteggio e paginazione restano coerenti.
      // Giacenza NULL esclusa, come la giacenza 0.
      if (soloDisponibili) q = q.gt('stock_quantity', 0);

      const term = query.trim();
      if (term.length >= 2) {
        // Virgole e parentesi spezzerebbero la sintassi del filtro PostgREST,
        // % e \ sono wildcard/escape di ilike: vanno neutralizzati.
        const safe = term.replace(/[,()%\\]/g, ' ').trim();
        if (safe) q = q.or(`name.ilike.%${safe}%,sku.ilike.%${safe}%`);
      }

      const from = page * PRODUCTS_PAGE_SIZE;
      const { data, error, count } = await q
        .order('name', { ascending: true })
        .range(from, from + PRODUCTS_PAGE_SIZE - 1);

      if (requestId !== productsRequestRef.current) return;

      if (error) {
        console.error('[EnhancedDealServiceSelector] load products failed', error);
        setProductsError('Non riesco a caricare gli articoli.');
        return;
      }

      const rows = (data ?? []) as ProductItem[];
      rows.forEach(p => productsCacheRef.current.set(p.id, p));
      setProducts(prev => {
        if (page === 0) return rows;
        // La paginazione puo' restituire un id gia' presente se il catalogo
        // cambia sotto i piedi: dedup per non avere key React duplicate.
        const visti = new Set(prev.map(p => p.id));
        return [...prev, ...rows.filter(p => !visti.has(p.id))];
      });
      setProductsPage(page);
      setProductsTotal(count ?? null);
    } catch (err) {
      if (requestId !== productsRequestRef.current) return;
      console.error('[EnhancedDealServiceSelector] load products failed', err);
      setProductsError('Non riesco a caricare gli articoli.');
    } finally {
      if (requestId === productsRequestRef.current) {
        setProductsLoading(false);
        setProductsLoadingMore(false);
      }
    }
  }, [organizationId]);

  // Prima pagina all'apertura del tab Prodotti e a ogni nuova ricerca.
  useEffect(() => {
    if (listMode !== 'product' || !organizationId) return;
    caricaProdotti(0, debouncedProductSearch, onlyInStock);
  }, [listMode, organizationId, debouncedProductSearch, onlyInStock, caricaProdotti]);

  const productsHasMore = productsTotal != null && products.length < productsTotal;

  const caricaAltriProdotti = React.useCallback(() => {
    if (!productsHasMore || productsLoading || productsLoadingMore) return;
    caricaProdotti(productsPage + 1, debouncedProductSearch, onlyInStock);
  }, [productsHasMore, productsLoading, productsLoadingMore, productsPage, debouncedProductSearch, onlyInStock, caricaProdotti]);

  // Infinite scroll sulla lista di sinistra: quando il fondo si avvicina,
  // chiedi la pagina successiva.
  const handleListScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (listMode !== 'product') return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      caricaAltriProdotti();
    }
  }, [listMode, caricaAltriProdotti]);

  const filteredServices = services.filter(service =>
    // First apply category filter
    (categoryFilter === null || service.category_id === categoryFilter) &&
    // Then apply search filter
    (service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (service.short_description && service.short_description.toLowerCase().includes(searchTerm.toLowerCase())))
  );
  
  // Get all unique categories
  const categories = React.useMemo(() => {
    const uniqueCategories = new Map();
    services.forEach(service => {
      if (service.category && !uniqueCategories.has(service.category.id)) {
        uniqueCategories.set(service.category.id, service.category);
      }
    });
    return Array.from(uniqueCategories.values());
  }, [services]);

  // Inizializza/aggiorna flag scroll categorie quando cambiano categorie,
  // listMode (Servizi/Prodotti) o viewport resize.
  useEffect(() => {
    if (listMode !== 'service') return;
    // requestAnimationFrame: aspetta che il DOM sia layoutato
    const raf = requestAnimationFrame(updateCategoriesScrollFlags);
    const onResize = () => updateCategoriesScrollFlags();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [listMode, categories.length]);

  // Save to localStorage DEBOUNCED quando selectedServices changes - evita interferenze durante la digitazione
  useEffect(() => {
    // DEBOUNCE il salvataggio a 3 secondi per ridurre conflitti durante l'editing
    const timeoutId = setTimeout(() => {
      if (dealId) {
        localStorage.setItem(`deal_${dealId}_services`, JSON.stringify(selectedServices));
      } else {
        localStorage.setItem('deal_draft_services', JSON.stringify(selectedServices));
      }
    }, 3000);
    
    return () => clearTimeout(timeoutId);
  }, [selectedServices, dealId]);

  // Load from localStorage on component mount
  useEffect(() => {
    // NON sovrascrivere se il parent ha già dei servizi - sono la fonte di verità
    // Questo previene la perdita di servizi quando si naviga tra step 1 e step 2
    if (selectedServices.length > 0) {
      console.log('🛒 Parent già ha servizi, skip localStorage load:', selectedServices.length);
      return;
    }

    const savedServices = dealId
      ? localStorage.getItem(`deal_${dealId}_services`)
      : localStorage.getItem('deal_draft_services');

    if (savedServices) {
      try {
        const parsedServices = JSON.parse(savedServices);
        if (parsedServices.length > 0) {
          console.log('📂 Caricamento servizi da localStorage:', parsedServices.length);
          onChange(parsedServices);
        }
      } catch (error) {
        console.error('Error parsing saved services', error);
      }
    }
  }, []);

  // Tieni traccia dell'ultimo indice valido per evitare reset durante la modifica
  const lastValidIndexRef = useRef<number | null>(null);

  // Sync editingService when externalSelectedServiceIndex changes
  useEffect(() => {
    console.log("🔄 External index changed to:", externalSelectedServiceIndex, "Last valid:", lastValidIndexRef.current);

    // Solo quando l'indice esterno cambia rispetto all'ultimo valido processato
    if (externalSelectedServiceIndex !== lastValidIndexRef.current) {
      lastValidIndexRef.current = externalSelectedServiceIndex;

      if (externalSelectedServiceIndex !== null) {
        // Verifica che il servizio esista nell'array
        if (externalSelectedServiceIndex >= 0 && externalSelectedServiceIndex < selectedServices.length) {
          // Inizializza l'editing service per forzare l'apertura del pannello
          const serviceToEdit = selectedServices[externalSelectedServiceIndex];
          console.log("🔧 Setting editing service for external index:", serviceToEdit);
          
          // Crea una copia del servizio da modificare
          const serviceCopy = {...serviceToEdit};
          
          // IMPORTANTE: Le variabili sono già presenti nel serviceCopy dal selectedServices array
          // Non dobbiamo cercarle nel database, sono già qui se modificate localmente
          console.log("🔍 Service variables already in serviceCopy:", serviceCopy.variables);
          
          // Se il servizio non ha variabili locali ma ha un ID database, prova a caricarle dal dealServiceStore
          if (!serviceCopy.variables && serviceCopy.id && dealId) {
            try {
              // Solo come fallback, prova a trovare il servizio nel dealServiceStore
              const dealServiceStore = useDealServiceStore.getState();
              const serviceWithVariables = dealServiceStore.dealServices.find(ds => ds.id === serviceCopy.id);
              
              if (serviceWithVariables?.variables && serviceWithVariables.variables.length > 0) {
                console.log("✅ Fallback: Found variables in dealServiceStore:", serviceWithVariables.variables);
                serviceCopy.variables = serviceWithVariables.variables;
              } else {
                console.log("📭 No variables found in dealServiceStore for service:", serviceCopy.id);
              }
            } catch (error) {
              console.error("❌ Error loading variables from dealServiceStore:", error);
            }
          }
          
          // Se è un servizio personalizzato, sincronizziamo il campo customServiceName e il tipo
          if (serviceCopy.service_id.toString().startsWith('custom-') && serviceCopy.custom_service_name) {
            console.warn("📎 [LOAD] Loading custom service name:", serviceCopy.custom_service_name);
            console.warn("📎 [LOAD] Full serviceCopy object:", JSON.stringify(serviceCopy, null, 2));

            // Imposta il nome personalizzato nel campo di input
            setCustomServiceName(serviceCopy.custom_service_name);

            // IMPORTANTE: Il metadata potrebbe arrivare come stringa JSON dal database
            // In quel caso dobbiamo fare il parse prima di usarlo
            let metadata = serviceCopy.metadata;
            console.warn("📎 [LOAD] Raw metadata (before parsing):", metadata);
            console.warn("📎 [LOAD] Metadata type:", typeof metadata);

            if (typeof metadata === 'string') {
              try {
                metadata = JSON.parse(metadata);
                console.warn("📎 [LOAD] Parsed metadata from JSON string:", metadata);
              } catch (e) {
                console.error("❌ [LOAD] Error parsing metadata JSON:", e, metadata);
                metadata = null;
              }
            }

            console.warn("📎 [LOAD] Metadata object:", metadata);
            console.warn("📎 [LOAD] Checking metadata.custom_service_type:", metadata?.custom_service_type);

            // Recupera il tipo di servizio da diverse fonti con ordine di priorità:
            // 1. metadata.custom_service_type (salvato dal database)
            // 2. serviceCopy.custom_service_type (presente in memoria)
            // 3. serviceCopy.service?.service_type (fallback per offerte vecchie)
            // 4. 'one_time' (default assoluto)
            const serviceType = metadata?.custom_service_type ||
                               serviceCopy.custom_service_type ||
                               serviceCopy.service?.service_type ||
                               'one_time';

            console.warn("📎 Determined service type:", serviceType);

            // Imposta il tipo nel serviceCopy
            serviceCopy.custom_service_type = serviceType;
            console.warn("✅ Loading custom service type:", serviceType);

            // IMPORTANTE: Sincronizza anche service.service_type per la visualizzazione
            if (serviceCopy.service) {
              serviceCopy.service.service_type = serviceType;
            } else {
              // Se service non esiste, crealo
              serviceCopy.service = {
                id: serviceCopy.service_id,
                title: serviceCopy.custom_service_name,
                description: '',
                service_type: serviceType
              };
            }
          } else {
            // Per servizi normali, resettiamo il campo custom
            setCustomServiceName('');
          }
          
          // Imposta le variabili nello stato locale se presenti
          if (serviceCopy.variables && Array.isArray(serviceCopy.variables)) {
            console.log("🔧 Setting service variables in local state:", serviceCopy.variables);
            setServiceVariables(serviceCopy.variables);
          } else {
            console.log("📭 No variables to set in local state");
            setServiceVariables([]);
          }

          // Inizializza useNextMonth basandosi sulle installments esistenti
          // payment_schedule può essere un array o un oggetto con installments
          const paymentSchedule = serviceCopy.payment_schedule as { installments?: DealPaymentInstallment[] } | DealPaymentInstallment[] | undefined;
          const installments = Array.isArray(paymentSchedule)
            ? paymentSchedule
            : paymentSchedule?.installments;
          const hasNextMonth = installments?.some(inst => inst.nextMonth === true);
          setUseNextMonth(hasNextMonth ?? false);

          setEditingService(serviceCopy);
          // Il gruppo Descrizione nasce aperto solo se c'è già del testo:
          // aprendo una riga vuota non si finisce davanti a un editor bianco.
          setDescOpen(!!serviceCopy.description?.trim());
          setRatesOpen(false);
          setOpenProp(null);
          // Salva una copia dell'originale per rilevare le modifiche
          setOriginalEditingService(JSON.parse(JSON.stringify(serviceCopy)));
        } else if (externalSelectedServiceIndex === -1) {
          // -1 indica un nuovo servizio
          // Non facciamo nulla qui perché il servizio sarà già stato impostato da handleSelectService
          console.log("🆕 New service mode (-1 index), no service reset needed");
          // Non sovrascriviamo l'editing service esistente
        } else {
          // Indice non valido, pulisci editing service
          console.log("❌ Invalid index, clearing editing service");
          setEditingService(null);
          setCustomServiceName('');
        }
      } else {
        // Indice null, pulisci editing service
        console.log("❌ Null index, clearing editing service");
        setEditingService(null);
        setCustomServiceName('');
      }
    } else {
      console.log("🔒 Skipping editingService update - index hasn't changed");
    }
  }, [externalSelectedServiceIndex, selectedServices]);

  // Sincronizza le modifiche di editingService con selectedServices in tempo reale
  useEffect(() => {
    if (editingService && externalSelectedServiceIndex !== null && externalSelectedServiceIndex >= 0 && externalSelectedServiceIndex < selectedServices.length) {
      console.log("🔄 Syncing editingService changes to selectedServices", {
        editingService_type: editingService.custom_service_type,
        editingService_id: editingService.service_id,
        index: externalSelectedServiceIndex,
        customServiceName
      });
      
      const currentService = selectedServices[externalSelectedServiceIndex];
      const isCustomService = editingService.service_id.startsWith('custom-');
      
      // Verifica se effettivamente c'è una differenza per evitare loop
      const needsUpdate = isCustomService && (
        currentService.custom_service_type !== editingService.custom_service_type ||
        currentService.custom_service_name !== customServiceName ||
        (currentService.metadata?.custom_service_type !== editingService.custom_service_type)
      );
      
      if (needsUpdate) {
        console.log("✅ Update needed, applying changes");
        const newSelectedServices = [...selectedServices];
        // Crea una copia aggiornata del servizio con le modifiche correnti
        const updatedService = { ...editingService };
        
        // Per servizi personalizzati, assicuriamoci che i metadata siano aggiornati
        if (isCustomService) {
          const serviceType = editingService.custom_service_type || 'one_time';
          
          // Imposta il tipo sia sul campo principale che nei metadata
          updatedService.custom_service_type = serviceType;
          
          if (!updatedService.metadata) {
            updatedService.metadata = {};
          }
          updatedService.metadata.custom_service_type = serviceType;
          updatedService.custom_service_name = customServiceName;
          
          // Aggiorna anche l'oggetto service se presente
          if (updatedService.service) {
            updatedService.service.service_type = serviceType;
            updatedService.service.title = customServiceName;
          }
          
          console.log("📋 Updated service data:", {
            custom_service_type: updatedService.custom_service_type,
            metadata_type: updatedService.metadata?.custom_service_type,
            service_type: updatedService.service?.service_type
          });
        }
        
        newSelectedServices[externalSelectedServiceIndex] = updatedService;
        onChange(newSelectedServices);
      } else {
        console.log("⏭️ No update needed");
      }
    }
  }, [editingService?.custom_service_type, customServiceName, externalSelectedServiceIndex]);

  // Load variables from DATABASE for existing deals
  // NOTA: Questo useEffect gestisce SOLO il caricamento dal database.
  // - Per NUOVI servizi (-1): le variabili sono caricate da handleSelectService
  // - Per servizi ESISTENTI nell'array: le variabili sono caricate da useEffect #1 (linea 225)
  useEffect(() => {
    const loadServiceVariablesFromDB = async () => {
      // FIX: Per i nuovi servizi (-1), le variabili sono già state caricate
      // da handleSelectService. Non dobbiamo interferire.
      if (externalSelectedServiceIndex === -1) {
        console.log('⏭️ Skipping DB load - new service mode, variables handled by handleSelectService');
        return;
      }

      // FIX: Se non c'è un indice valido, esci
      if (externalSelectedServiceIndex === null || externalSelectedServiceIndex < 0) {
        console.log('⏭️ Skipping DB load - no valid service index');
        return;
      }

      const selectedService = selectedServices[externalSelectedServiceIndex];

      // Se il servizio ha già variabili in memoria (caricate da useEffect #1), non caricare dal DB
      if (selectedService?.variables && selectedService.variables.length > 0) {
        console.log('⏭️ Variables already in memory, skipping DB load');
        return;
      }

      // Se il servizio ha un ID database e siamo in un deal esistente,
      // prova a caricare dal database
      if (selectedService?.id && dealId) {
        try {
          console.log('🔍 Fetching variables from database for service:', selectedService.id);
          const { fetchServiceVariables, getServiceVariablesByServiceId } = useDealVariableStore.getState();
          await fetchServiceVariables(selectedService.id);
          const loadedVariables = getServiceVariablesByServiceId(selectedService.id);

          if (loadedVariables.length > 0) {
            console.log('✅ Loaded variables from database:', loadedVariables);
            setServiceVariables(loadedVariables);
          }
        } catch (error) {
          console.error('❌ Error loading service variables from DB:', error);
        }
      }
    };

    loadServiceVariablesFromDB();
  }, [externalSelectedServiceIndex, dealId]);  // ← Rimosso editingService?.id e selectedServices

  // Handle variables changes
  const handleVariablesChange = (variables: DealVariable[]) => {
    setServiceVariables(variables);
    
    // Update the editing service with the new variables
    if (editingService) {
      setEditingService({
        ...editingService,
        variables: variables
      });
    }

    // Also update the selectedServices array if we're editing an existing service
    if (externalSelectedServiceIndex !== null && externalSelectedServiceIndex >= 0) {
      const updatedServices = [...selectedServices];
      updatedServices[externalSelectedServiceIndex] = {
        ...updatedServices[externalSelectedServiceIndex],
        variables: variables
      };
      onChange(updatedServices);
    }
  };

  // Calculate total amount
  const calculateTotalAmount = () => {
    return selectedServices.reduce((total, service) => {
      const baseAmount = service.discounted_amount ?? service.amount;
      
      // If it's a monthly service with duration > 0, multiply by number of months
      const isMonthlyService = services.find(s => s.id === service.service_id)?.service_type === 'monthly';
      if (isMonthlyService && service.monthly_duration && service.monthly_duration > 0) {
        return total + (baseAmount * service.monthly_duration);
      }
      
      return total + baseAmount;
    }, 0);
  };

  // VIVA: handler selezione prodotto magazzino — aggiunge una riga
  // deal_services con line_type='product' (no payment_method richiesto)
  const handleSelectProduct = (product: ProductItem) => {
    const unitPrice = Number(product.regular_price ?? 0);
    const defaultQty = 1;
    const newProductLine: DealServiceFormData = {
      service_id: `product-${product.id}-${Date.now()}`, // ID transitorio (non FK servizio reale)
      line_type: 'product',
      product_id: product.id,
      quantity: defaultQty,
      // L'unita' la decide l'anagrafica dell'articolo: chi vende a kg o a
      // bancali non deve correggere 'pz' su ogni riga.
      // Articolo senza UDM: l'unità predefinita dell'org, se impostata, al posto di 'pz'
      unit_of_measure: orgDefaultUnit ? (product.unit_of_measure_1?.trim() || orgDefaultUnit) : resolveProductUnit(product),
      unit_price: unitPrice,
      amount: unitPrice * defaultQty,
      // Nessuno sconto all'inserimento: null, non "scontato quanto il pieno".
      // Preimpostarlo uguale ad amount faceva credere a tutto il resto del
      // pannello che uno sconto ci fosse gia'.
      discounted_amount: null,
      custom_service_name: product.name,
      description: product.sku ? `SKU: ${product.sku}` : '',
    };
    const updated = [...(selectedServices ?? []), newProductLine];
    onChange(updated);
    // Apri direttamente l'editing del prodotto appena aggiunto
    const newIndex = updated.length - 1;
    onExternalServiceIndexChange(newIndex);
    setEditingService(newProductLine);
    setOriginalEditingService(newProductLine);
    toast.success(`Prodotto "${product.name}" aggiunto`);
    // Scroll del pannello editor in cima per mostrare subito i campi.
    requestAnimationFrame(() => {
      editorScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  // Handle selecting a service from the left sidebar
  const handleSelectService = async (service: Service) => {
    // Cerca di determinare il metodo di pagamento predefinito
    // Prima controlla se il servizio ha un metodo di pagamento predefinito
    let defaultPaymentMethodId = '';
    
    // Verifica se il servizio ha un metodo di pagamento predefinito
    if (service.default_payment_method_id) {
      console.log("🔍 Trovato metodo di pagamento predefinito nel servizio:", service.default_payment_method_id);
      defaultPaymentMethodId = service.default_payment_method_id;
    } else {
      // Se il servizio non ha un metodo di pagamento predefinito, cerca nel cliente associato al deal
      const clientId = formData?.client_id;
      if (clientId) {
        // Cerchiamo il cliente direttamente tramite la prop clients dal parent component
        // Ciò evita l'uso di require che non è disponibile in ambiente ES modules
        const clients = formData?.clients || [];
        const client = clients.find(c => c.id === clientId);
        
        if (client?.default_payment_method_id) {
          console.log("🔍 Trovato metodo di pagamento predefinito nel cliente:", client.default_payment_method_id);
          defaultPaymentMethodId = client.default_payment_method_id;
        } else {
          console.log("⚠️ Nessun metodo di pagamento predefinito trovato nel servizio o nel cliente");
        }
      }
    }
    
    // Creiamo sempre un nuovo servizio, indipendentemente dal fatto che esista già nella lista
    // Questo permette di aggiungere lo stesso servizio più volte nella stessa proposta
    const isCustomService = service.id.startsWith('custom-');
    const initialServiceType = isCustomService ? 'one_time' : service.service_type;

    const newService: DealServiceFormData = {
      service_id: service.id,
      payment_method_id: defaultPaymentMethodId, // Usiamo il metodo di pagamento predefinito se disponibile
      amount: service.list_price || 0,
      description: service.description || service.short_description || '',
      monthly_duration: null,
      // Per servizi personalizzati, inizializziamo il campo per il nome personalizzato
      custom_service_name: isCustomService ? '' : undefined,
      custom_service_type: isCustomService ? 'one_time' : undefined,

      // Includiamo l'attributo auto_invoice dal servizio originale
      // Per i servizi personalizzati, lo impostiamo a false
      service: {
        id: service.id,
        title: service.title,
        description: service.description || '',
        short_description: service.short_description,
        service_type: initialServiceType,
        auto_invoice: service.auto_invoice
      }
    };
    
    // Se è un servizio personalizzato, resettiamo lo stato del nome
    if (service.id.startsWith('custom-')) {
      setCustomServiceName('');
    }

    // Carica automaticamente le variabili template per i servizi normali (non personalizzati)
    let templateVariables: DealVariable[] = [];
    if (!service.id.startsWith('custom-')) {
      try {
        console.log('🔍 DEBUG: Caricamento variabili template per servizio:', service.id, service.title);
        const { getTemplatesByServiceId, fetchTemplates } = useServiceVariableTemplateStore.getState();

        // Prima prova a vedere se ci sono template in memoria
        let templates = getTemplatesByServiceId(service.id);
        console.log('🔍 DEBUG: Template in memoria:', templates.length);

        // Se non ci sono template in memoria, prova a caricarli dal database
        // OTTIMIZZAZIONE: Ora lo store accumula i template invece di sovrascriverli,
        // quindi i template di servizi diversi coesistono nello store
        if (templates.length === 0) {
          console.log('🔍 DEBUG: Nessun template in memoria, carico dal database...');
          await fetchTemplates(service.id);
          templates = getTemplatesByServiceId(service.id);
          console.log('🔍 DEBUG: Template caricati dal database:', templates.length);

          if (templates.length > 0) {
            console.log('✅ DEBUG: Template trovati:', templates);
          } else {
            console.log('📭 DEBUG: Nessun template trovato per questo servizio nel database');
          }
        } else {
          console.log('✅ DEBUG: Template trovati in cache, skip chiamata al database');
        }
        
        // Converti i template in variabili deal
        if (templates.length > 0) {
          templateVariables = templates.map((template, index) => {
            const variable = {
              id: `template-${template.id}-${Date.now()}`, // Genera un ID temporaneo per la variabile
              deal_service_id: '', // Sarà impostato quando il servizio viene salvato
              organization_id: '', // Sarà impostato quando il servizio viene salvato
              name: template.name,
              description: template.description || '',
              amount: template.amount,
              type: template.type,
              is_cost: template.is_cost,
              is_internal_cost: template.is_internal_cost ?? template.is_cost ?? false,
              is_vat_exempt: template.is_vat_exempt,
              is_stamp_duty: false,
              is_stamp_duty_art15: template.is_stamp_duty_art15 ?? false,
              is_confirmed: false,
              sort_order: template.sort_order ?? index,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            console.log('🔄 DEBUG: Template convertito in variabile:', template.name, '→', variable);
            return variable;
          });
          
          // Aggiungi le variabili template al servizio
          newService.variables = templateVariables;
          console.log('🎯 DEBUG: Applicate', templateVariables.length, 'variabili template al servizio');
          console.log('🎯 DEBUG: newService.variables:', newService.variables);
        } else {
          console.log('📭 DEBUG: Nessuna variabile template da applicare');
        }
      } catch (error) {
        console.error('❌ DEBUG: Errore nel caricamento variabili template:', error);
        // Mostra un alert per debug
        alert(`Errore caricamento template: ${error.message || error}`);
      }
    } else {
      console.log('🔍 DEBUG: Servizio personalizzato, skip template loading');
    }

    // Carica automaticamente i template di costi per i servizi normali (non personalizzati)
    let templateCosts: any[] = [];
    if (!service.id.startsWith('custom-')) {
      try {
        console.log('🔍 DEBUG: Caricamento template costi per servizio:', service.id, service.title);
        const { getTemplatesByServiceId, fetchTemplates } = useServiceCostTemplateStore.getState();
        
        // Prima prova a vedere se ci sono template in memoria
        let costTemplates = getTemplatesByServiceId(service.id);
        console.log('🔍 DEBUG: Template costi in memoria:', costTemplates.length);
        
        // Se non ci sono template in memoria, prova a caricarli dal database
        if (costTemplates.length === 0) {
          console.log('🔍 DEBUG: Nessun template costi in memoria, carico dal database...');
          await fetchTemplates(service.id);
          costTemplates = getTemplatesByServiceId(service.id);
          console.log('🔍 DEBUG: Template costi caricati dal database:', costTemplates.length);
        }
        
        // Converti i template costi in DealServiceCost
        if (costTemplates.length > 0) {
          templateCosts = costTemplates.map(template => ({
            id: `template-${template.id}-${Date.now()}`, // ID temporaneo
            deal_service_id: null, // Sarà impostato quando il servizio viene salvato
            template_id: template.id,
            name: template.name,
            description: template.description,
            amount: template.default_amount,
            cost_category_id: template.cost_category_id,
            calculation_type: template.calculation_type,
            is_visible_to_client: template.is_visible_to_client,
            include_in_pdf: template.include_in_pdf,
            cost_display_name: template.cost_display_name,
            sort_order: template.sort_order,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            cost_category: template.cost_category,
            template: template
          }));
          
          // Aggiungi i costi template al servizio
          newService.service_costs = templateCosts;
          console.log('🎯 DEBUG: Applicati', templateCosts.length, 'template costi al servizio');
        } else {
          console.log('📭 DEBUG: Nessun template costi da applicare');
        }
      } catch (error) {
        console.error('❌ DEBUG: Errore nel caricamento template costi:', error);
        // Per ora solo log, non blocchiamo la creazione del servizio
      }
    }

    // Se è stato trovato un metodo di pagamento predefinito, creiamo il payment_schedule
    // PRIMA di pushare il servizio nella lista, così la riga è già completa.
    if (defaultPaymentMethodId) {
      const method = paymentMethods.find(m => m.id === defaultPaymentMethodId);
      if (method) {
        const schedule = {
          installments: method.payment_schedule.installments.map(inst => ({
            ...inst,
            amount: inst.type === 'percentage'
              ? (newService.amount * (inst.value / 100))
              : inst.value,
            fixedDate: computeFixedDateFromTemplate(inst)
          }))
        };
        newService.payment_schedule = schedule;
      }
    }

    // Variabili template caricate per il pannello laterale
    setServiceVariables(templateVariables);

    // Editor mostra l'oggetto appena composto
    setEditingService(newService);
    setOriginalEditingService(newService);

    // UX coerente con prodotti: aggiungiamo SUBITO il servizio alla lista
    // (così il "✓" appare nella card del catalogo) e apriamo l'editor sulla
    // riga appena creata. L'editor auto-salva via useEffect debounced. Per
    // rimuovere c'è il bottone "Rimuovi" nell'header del pannello dettaglio.
    const updatedList = [...(selectedServices ?? []), newService];
    onChange(updatedList);
    const newIndex = updatedList.length - 1;
    lastValidIndexRef.current = newIndex;
    onExternalServiceIndexChange(newIndex);

    // Toast di conferma (Notion-like). I servizi custom mostrano placeholder.
    const isCustomNew = service.id.startsWith('custom-');
    const displayName = isCustomNew
      ? (service.title || 'Servizio personalizzato')
      : (service.title || 'Servizio');
    toast.success(`${isCustomNew ? 'Servizio personalizzato' : 'Servizio'} "${displayName}" aggiunto`);
    // Scroll del pannello editor in cima per mostrare subito i campi.
    requestAnimationFrame(() => {
      editorScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });

    // Force re-render of the editor panel with the new service
    setTimeout(() => {
      // This makes sure the UI updates properly after the service selection
      if (serviceListRef.current) {
        // Optional - scroll to the editor area on mobile
        if (window.innerWidth < 768) {
          window.scrollTo({
            top: serviceListRef.current.offsetTop,
            behavior: 'smooth'
          });
        }
      }
    }, 100) // Aumentato a 100ms per sicurezza
  };

  // Funzione per rilevare se ci sono modifiche non salvate
  const hasUnsavedChanges = () => {
    if (!editingService || !originalEditingService) return false;

    // Confronta i campi principali del servizio
    return (
      editingService.amount !== originalEditingService.amount ||
      editingService.discounted_amount !== originalEditingService.discounted_amount ||
      editingService.payment_method_id !== originalEditingService.payment_method_id ||
      editingService.monthly_duration !== originalEditingService.monthly_duration ||
      editingService.description !== originalEditingService.description ||
      editingService.custom_service_type !== originalEditingService.custom_service_type ||
      customServiceName !== originalEditingService.custom_service_name ||
      ((editingService.metadata as any)?.billing_interval_months ?? null) !== ((originalEditingService.metadata as any)?.billing_interval_months ?? null) ||
      JSON.stringify(serviceVariables) !== JSON.stringify(originalEditingService.variables || []) ||
      JSON.stringify(editingService.addons || []) !== JSON.stringify(originalEditingService.addons || [])
    );
  };

  // Propaga lo stato delle modifiche non salvate al parent
  useEffect(() => {
    onUnsavedChangesChange(hasUnsavedChanges());
  }, [editingService, originalEditingService, customServiceName, serviceVariables]);

  // Handle saving a service (either adding a new one or updating an existing one)
  const handleSaveService = () => {
    if (!editingService) return;

    // Flush sincrono della descrizione: se l'utente clicca "Salva" mentre il
    // testo è ancora nel debounce dell'editor TipTap, recuperiamo l'HTML appena
    // committato e lo applichiamo direttamente, senza dover attendere il giro di
    // setState asincrono di React (che altrimenti farebbe salvare la descrizione
    // vecchia mentre il prezzo — sincrono — viene salvato).
    const flushedDescription = descEditorRef.current?.flush();

    // Per i servizi personalizzati, verifichiamo che sia stato inserito un nome
    if (editingService.service_id.startsWith('custom-') && (!customServiceName || customServiceName.trim() === '')) {
      // Show error message
      alert(t.customServiceNameRequired || 'Inserisci un nome per il servizio personalizzato');
      return;
    }

    // Categoria obbligatoria sulle righe scritte a mano: senza, la riga non entra
    // nelle liste e nei report per servizio acquistato.
    if (
      editingService.service_id.startsWith('custom-') &&
      categorieServizio.length > 0 &&
      !editingService.category_id
    ) {
      alert('Scegli la categoria del servizio: senza, il cliente resta fuori dalle segmentazioni per acquisto');
      return;
    }
    
    // Debug - Mostra lo stato del servizio prima del salvataggio
    console.log("📋 Stato del servizio prima del salvataggio:", {
      serviceId: editingService.service_id,
      isCustom: editingService.service_id.startsWith('custom-'),
      customServiceName: customServiceName,
      serviceTitle: editingService.service?.title,
      custom_service_name: editingService.custom_service_name
    });

    // Validate: serve un metodo di pagamento OPPURE rate personalizzate valide.
    // Le righe prodotto (line_type='product') NON richiedono nessuno dei due — sono vendite una-tantum a quantità.
    const isProductLine = editingService.line_type === 'product';
    const hasCustomSchedule = (editingService.payment_schedule?.installments?.length ?? 0) > 0 && !editingService.payment_method_id;
    if (!isProductLine && !editingService.payment_method_id && !hasCustomSchedule) {
      alert(t.selectPaymentMethodError || 'Seleziona un metodo di pagamento o configura rate personalizzate prima di aggiungere il servizio');
      return;
    }
    if (!isProductLine && hasCustomSchedule) {
      const serviceAmount = editingService.discounted_amount ?? editingService.amount;
      const totalScheduled = editingService.payment_schedule!.installments!.reduce((sum, inst) => sum + (Number.isFinite(inst.amount) ? Number(inst.amount) : 0), 0);
      if (Math.abs(totalScheduled - serviceAmount) > 0.01) {
        alert(`Il totale delle rate personalizzate (€${totalScheduled.toFixed(2)}) non coincide con l'importo del servizio (€${serviceAmount.toFixed(2)}). Modifica le rate prima di salvare.`);
        return;
      }
    }

    const newSelectedServices = [...selectedServices];

    // Crea una copia del servizio con eventuali modifiche per i servizi personalizzati
    const serviceToSave = {...editingService};

    // Applica la descrizione flushata (se c'era testo in volo nel debounce).
    // flush() ritorna null se non c'era nulla in sospeso → manteniamo il valore
    // già presente in editingService.
    if (flushedDescription != null) {
      serviceToSave.description = flushedDescription;
    }

    // FIX 2026-07-10 (Finup): riallinea gli importi delle rate PERCENTUALI del metodo di
    // pagamento all'importo corrente del servizio. Senza questo, modificando il prezzo dopo
    // aver scelto il metodo, le rate restavano con l'amount calcolato sul prezzo vecchio
    // (es. rata "100%" da 1000€ su un servizio da 500€) e la fatturazione delle rate fisse
    // avrebbe usato l'importo stantio. Le rate 'fixed' (importi assoluti) non vengono toccate.
    if (serviceToSave.payment_method_id && serviceToSave.payment_schedule?.installments?.length) {
      const effectiveAmount = serviceToSave.discounted_amount ?? serviceToSave.amount ?? 0;
      serviceToSave.payment_schedule = {
        ...serviceToSave.payment_schedule,
        installments: serviceToSave.payment_schedule.installments.map(inst =>
          inst.type === 'percentage'
            ? { ...inst, amount: +(effectiveAmount * ((inst.value || 0) / 100)).toFixed(2) }
            : inst
        )
      };
    }

    // Include variables in the service
    console.log("🔍 DEBUG VARIABILI - serviceVariables:", serviceVariables?.length || 0, serviceVariables);
    if (serviceVariables && serviceVariables.length > 0) {
      serviceToSave.variables = serviceVariables;
      console.log("✅ DEBUG VARIABILI - Assegnate al servizio:", serviceToSave.variables.map(v => ({ id: v.id, name: v.name })));
    } else {
      console.warn("⚠️ DEBUG VARIABILI - Nessuna variabile da assegnare!");
    }

    // Per debugging - prima del salvataggio
    console.log("⬇️ SERVIZIO PRIMA DEL SALVATAGGIO:", JSON.stringify(serviceToSave, null, 2));
    
    // Se è un servizio personalizzato, assegniamo il nome personalizzato
    if (editingService.service_id.startsWith('custom-')) {
      // Verifichiamo che il nome sia valido (già controllato nel check precedente)
      
      // Imposta il campo custom_service_name
      serviceToSave.custom_service_name = customServiceName;
      
      // SOLUZIONE DEFINITIVA MIGLIORATA
      // Invece di rimuovere il campo service, creiamolo o aggiorniamolo
      // Così sia questo componente che DealServiceList possono mostrare il nome corretto
      if (!serviceToSave.service) {
        serviceToSave.service = {
          id: serviceToSave.service_id,
          title: customServiceName, // Impostiamo il nome personalizzato anche qui
          description: '',
          service_type: editingService.custom_service_type || 'one_time'
        };
      } else {
        // Se service esiste già, aggiorniamo il titolo e il tipo
        serviceToSave.service.title = customServiceName;
        serviceToSave.service.service_type = editingService.custom_service_type || 'one_time';
      }
      
      // Rimossa la logica che impediva di avere amount a 0
      // Ora permettiamo di salvare servizi con prezzo 0 euro
      
      // Aggiungiamo una descrizione di fallback se non esiste
      serviceToSave.description = serviceToSave.description || `Servizio personalizzato: ${customServiceName}`;
      
      // Salviamo il tipo di servizio personalizzato nei metadata per persistenza nel database
      const serviceType = editingService.custom_service_type || 'one_time';
      serviceToSave.custom_service_type = serviceType;
      
      if (!serviceToSave.metadata) {
        serviceToSave.metadata = {};
      }
      serviceToSave.metadata.custom_service_type = serviceType;
      
      // Log esplicito per debugging
      console.log("🔍 SOLUZIONE DEFINITIVA MIGLIORATA - Servizio personalizzato:");
      console.log("Nome:", customServiceName);
      console.log("Service ID:", serviceToSave.service_id);
      console.log("custom_service_name:", serviceToSave.custom_service_name);
      console.log("custom_service_type:", editingService.custom_service_type);
      console.log("metadata:", serviceToSave.metadata);
      console.log("service:", JSON.stringify(serviceToSave.service, null, 2));
      console.log("È un servizio personalizzato:", serviceToSave.service_id.startsWith('custom-'));
    }

    if (externalSelectedServiceIndex === -1) {
      // Adding a new service
      console.log("➕ Adding new service to list");
      newSelectedServices.push(serviceToSave);
    } else if (externalSelectedServiceIndex !== null &&
               externalSelectedServiceIndex >= 0 &&
               externalSelectedServiceIndex < selectedServices.length) {
      // Updating an existing service
      console.log("✏️ Updating existing service at index:", externalSelectedServiceIndex);
      newSelectedServices[externalSelectedServiceIndex] = serviceToSave;
    } else {
      console.warn("⚠️ Unexpected index state when saving service:", externalSelectedServiceIndex);
    }

    onChange(newSelectedServices);

    // Determina l'indice del servizio salvato
    const savedServiceIndex = externalSelectedServiceIndex === -1
      ? newSelectedServices.length - 1
      : externalSelectedServiceIndex;

    // Notifica il parent che il servizio è stato salvato per il feedback visivo
    if (savedServiceIndex !== null && savedServiceIndex >= 0) {
      onServiceSaved(savedServiceIndex);
    }

    // Aggiorna il riferimento dell'ultimo indice valido prima di azzerare l'indice
    lastValidIndexRef.current = null;
    // Resettiamo lo stato per il prossimo servizio personalizzato
    setCustomServiceName('');
    // Reset checkbox "prossimo mese"
    setUseNextMonth(false);
    // Chiudiamo esplicitamente il pannello di editing
    setEditingService(null);
    setOriginalEditingService(null);
    // Reset del tab quando chiudi
    setActiveTab('details');
    // Non uso più setSelectedServiceIndex dato che è stato rimosso
    onExternalServiceIndexChange(null);
  };

  // Auto-save: propaga `editingService` (+ variables + custom name) alla
  // riga di `selectedServices` corrispondente, senza chiudere il pannello e
  // senza alert di validazione. La persistenza a DB resta delegata al bottone
  // "Salva offerta" piu' a monte. Debounce 400ms per non spammare a ogni
  // keypress; cancellato al unmount o al cambio servizio.
  useEffect(() => {
    if (!editingService) return;
    const idx = externalSelectedServiceIndex;
    if (idx === null || idx === undefined || idx < 0 || idx >= selectedServices.length) return;

    const t = setTimeout(() => {
      const merged: DealServiceFormData = { ...editingService };
      // Per servizi custom propaga anche il nome editato live
      if (editingService.service_id?.startsWith('custom-')) {
        merged.custom_service_name = customServiceName || merged.custom_service_name;
      }
      if (serviceVariables && serviceVariables.length > 0) {
        merged.variables = serviceVariables;
      }
      // Allineamento dell'originale al nuovo "saved snapshot" cosi'
      // hasUnsavedChanges() torna false dopo l'auto-save. Va eseguito SEMPRE,
      // anche se selectedServices[idx] è già identico a `merged` (caso in cui
      // skippiamo l'onChange per evitare render loop) — altrimenti l'originale
      // resta indietro e il parent (DealForm) vede sempre "modifiche pendenti"
      // bloccando il submit del bottone "Salva offerta".
      const current = selectedServices[idx];
      const mergedAligned = JSON.parse(JSON.stringify(merged));
      setOriginalEditingService(mergedAligned);
      if (JSON.stringify(current) === JSON.stringify(merged)) return;
      const next = selectedServices.slice();
      next[idx] = merged;
      onChange(next);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingService, serviceVariables, customServiceName, externalSelectedServiceIndex]);

  // Handle removing a service
  const handleRemoveService = (index: number) => {
    console.log("🗑️ Removing service at index:", index);
    const newSelectedServices = [...selectedServices];
    newSelectedServices.splice(index, 1);
    onChange(newSelectedServices);

    // Aggiornamento dell'indice del servizio selezionato - uso solo l'indice esterno
    if (externalSelectedServiceIndex === index) {
      // Se il servizio rimosso è quello che stiamo attualmente modificando, deselezionalo
      console.log("🚫 Deselecting current service (was removed)");
      lastValidIndexRef.current = null;
      onExternalServiceIndexChange(null);
    } else if (externalSelectedServiceIndex !== null && externalSelectedServiceIndex > index) {
      // Se il servizio rimosso è prima di quello che stiamo modificando, aggiorna l'indice
      const newIndex = externalSelectedServiceIndex - 1;
      console.log("⬆️ Adjusting selected index after removal:", newIndex);
      lastValidIndexRef.current = newIndex;
      onExternalServiceIndexChange(newIndex);
    }
  };

  // Handle payment method change
  const handlePaymentMethodChange = (paymentMethodId: string) => {
    console.log('🔄 EnhancedDealServiceSelector: Payment method change:', paymentMethodId);
    if (!editingService) return;

    const method = paymentMethods.find(m => m.id === paymentMethodId);
    if (!method) {
      console.log('❌ Payment method not found:', paymentMethodId);
      return;
    }
    console.log('✅ Payment method found:', method.name);

    const methodInstallments = extractMethodInstallments(method);
    const hasConfigurableInstallments = methodInstallments.some(
      inst => inst.isConfigurable === true
    );

    // Se il metodo di pagamento è lo stesso, preserva le rate esistenti
    // e apri solo il gruppo "Rate e scadenze" se ci sono rate configurabili
    if (editingService.payment_method_id === paymentMethodId) {
      console.log('ℹ️ Stesso metodo di pagamento, preservo le rate esistenti');
      if (hasConfigurableInstallments) {
        setRatesOpen(true);
      }
      return;
    }

    const currentAmount = editingService.discounted_amount ?? editingService.amount;

    // Reset useNextMonth se il nuovo metodo non ha rate con giorno fisso
    const hasFixedDay = methodInstallments.some(inst => inst.fixedDayOfMonth);
    if (!hasFixedDay) {
      setUseNextMonth(false);
    }

    // Genera nuovo schedule solo se il metodo è effettivamente cambiato
    // Crea la struttura corretta per il database constraint
    const schedule = {
      installments: methodInstallments.map(inst => ({
        ...inst,
        amount: inst.type === 'percentage'
          ? (currentAmount * (inst.value / 100))
          : inst.value,
        nextMonth: inst.fixedDayOfMonth && useNextMonth ? true : undefined,
        fixedDate: computeFixedDateFromTemplate({ ...inst, nextMonth: inst.fixedDayOfMonth && useNextMonth ? true : undefined })
      }))
    };

    console.log('💰 EnhancedDealServiceSelector: Generated payment schedule:', {
      serviceAmount: currentAmount,
      methodName: method.name,
      installmentsCount: schedule.length,
      schedule: schedule
    });

    setEditingService({
      ...editingService,
      payment_method_id: paymentMethodId,
      payment_schedule: schedule
    });

    // Rate da configurare: prima si apriva da solo un drawer sopra il pannello,
    // rubando il posto a quello che l'utente stava facendo. Ora si apre il
    // gruppo "Rate e scadenze", che sta nel flusso della pagina.
    if (hasConfigurableInstallments) {
      setRatesOpen(true);
    }
  };

  // Handle payment schedule update
  const handleSaveSchedule = (installments: DealPaymentInstallment[]) => {
    if (!editingService) return;

    // Create a deep copy of installments
    const normalizedInstallments = JSON.parse(JSON.stringify(installments.map(inst => ({
      type: inst.type || 'fixed',
      value: typeof inst.value === 'number' ? inst.value : 0,
      amount: typeof inst.amount === 'number' ? inst.amount : 0,
      daysAfterStart: inst.daysAfterStart || 0,
      description: inst.description || '',
      isConfigurable: inst.isConfigurable || false,
      timeUnit: inst.timeUnit,
      fixedDayOfMonth: inst.fixedDayOfMonth,
      nextMonth: inst.nextMonth,
      fixedDate: inst.fixedDate,
      // Finup R21: preserva il flag "data da destinarsi"
      awaiting_approval: (inst as any).awaiting_approval ?? false
    }))));

    setEditingService({
      ...editingService,
      payment_schedule: { installments: normalizedInstallments }
    });

    setSheet(null);
  };

  // Check if selected payment method has configurable installments
  const canConfigureInstallments = () => {
    if (!editingService) return false;
    const selectedPaymentMethod = paymentMethods.find(m => m.id === editingService.payment_method_id);
    return extractMethodInstallments(selectedPaymentMethod).some(
      (inst: any) => inst.isConfigurable === true
    );
  };

  // ---------------------------------------------------------------------------
  // Economia della riga: un posto solo dove si calcolano base, add-on e totale.
  // `amount` include già gli add-on billable (vedi onChange del picker), quindi
  // la base si ottiene sottraendoli.
  // ---------------------------------------------------------------------------
  const billableAddonsTotal = (svc: DealServiceFormData | null): number =>
    (svc?.addons ?? [])
      .filter((a) => !a.is_included_in_base)
      .reduce((s, a) => s + (Number.isFinite(a.amount) ? Number(a.amount) : 0), 0);

  /** Prezzo della riga al netto degli add-on: quello che il commerciale tratta. */
  const baseAmountOf = (svc: DealServiceFormData | null): number =>
    Math.max(0, (svc?.discounted_amount ?? svc?.amount ?? 0) - billableAddonsTotal(svc));

  /** Totale della riga, add-on inclusi: è ciò su cui quadrano le rate. */
  const rowTotalOf = (svc: DealServiceFormData | null): number =>
    Number(svc?.discounted_amount ?? svc?.amount ?? 0);

  /** Servizio a catalogo della riga in modifica (null per custom e prodotti). */
  const catalogServiceOf = (svc: DealServiceFormData | null): Service | undefined =>
    svc && !svc.service_id?.startsWith('custom-') ? services.find((s) => s.id === svc.service_id) : undefined;

  /** Tipo di ricorrenza effettivo, che il servizio sia a catalogo o custom. */
  const serviceTypeOf = (svc: DealServiceFormData | null): string | undefined =>
    svc?.service_id?.startsWith('custom-') ? svc.custom_service_type : catalogServiceOf(svc)?.service_type;

  /** Periodo di fatturazione corrente in mesi (1 se non ricorrente). */
  const billingMonthsOf = (svc: DealServiceFormData | null): number => {
    const bim = (svc?.metadata as any)?.billing_interval_months;
    if (bim && bim > 0) return bim;
    return serviceTypeOf(svc) === 'annual' ? 12 : 1;
  };

  /**
   * Importo di un add-on della riga riportato a un altro periodo. Il prezzo del
   * periodo lo decide il catalogo: `service_addons.amount` è il canone del
   * periodo base e `billing_prices` dice quanto costa l'annuale, il semestrale
   * e così via. Sugli snapshot orfani (add-on tolto dal catalogo, o creato al
   * volo dentro l'offerta) resta la proporzione lineare.
   */
  const addonAmountForPeriod = (
    sel: NonNullable<DealServiceFormData['addons']>[number],
    fromMonths: number,
    toMonths: number,
    stype?: string,
    /** true = prezzo pieno del periodo, senza applicare gli sconti per frequenza. */
    ignoreRules = false,
  ): number => {
    const master = sel.service_addon_id
      ? catalogAddons.find((a) => a.id === sel.service_addon_id)
      : undefined;
    const current = Number.isFinite(sel.amount) ? Number(sel.amount) : 0;
    if (!master) {
      const from = fromMonths || 1;
      return Math.round((current / from) * toMonths * 100) / 100;
    }
    const monthly = monthlyBaseOf(Number(master.amount) || 0, stype);
    if (ignoreRules) return fullPriceForPeriod(monthly, toMonths);
    return listPriceForPeriod(monthly, parseBillingPrices((master as any).billing_prices), toMonths);
  };

  /**
   * Cambio di frequenza. Il prezzo del nuovo periodo arriva dalle regole di
   * listino del catalogo (`billing_prices`); dove non ce ne sono, resta la
   * proporzione lineare di sempre. Se il prezzo è stato scritto a mano
   * (`metadata.price_override`) non lo tocchiamo: chi ha deciso quel numero non
   * se lo vede cambiare sotto le dita.
   *
   * Gli add-on seguono la riga: ognuno con le proprie regole, perché i servizi
   * che tengono il prezzo nei piani (base 0 + add-on) hanno lo sconto annuale
   * lì e non sul servizio.
   */
  const applyBillingInterval = (newMonths: number) => {
    if (!editingService) return;
    const meta: any = editingService.metadata || {};
    const curMonths = billingMonthsOf(editingService);
    if (newMonths === curMonths) { setOpenProp(null); return; }

    // Prezzo bloccato a mano: cambia solo il periodo, gli importi restano quelli
    // che il commerciale ha deciso — add-on compresi. Ricalcolarli qui
    // cancellerebbe lo sconto appena scritto.
    if (meta.price_override) {
      setEditingService({
        ...editingService,
        metadata: { ...meta, billing_interval_months: newMonths },
      });
      setOpenProp(null);
      return;
    }

    const svcRow = catalogServiceOf(editingService);
    const rules = parseBillingPrices((svcRow as any)?.billing_prices);
    const stype = serviceTypeOf(editingService);
    const base = baseAmountOf(editingService);

    let nextBase = base;
    if (curMonths > 0 && base > 0) {
      const monthly = monthlyBaseOf(base / curMonths, undefined);
      nextBase = listPriceForPeriod(monthly, rules, newMonths);
    }

    const nextAddons = (editingService.addons ?? []).map((sel) => ({
      ...sel,
      amount: addonAmountForPeriod(sel, curMonths, newMonths, stype),
    }));
    const nextAddonsTotal = nextAddons
      .filter((a) => !a.is_included_in_base)
      .reduce((s, a) => s + (Number.isFinite(a.amount) ? Number(a.amount) : 0), 0);

    const nextTotal = Math.round((nextBase + nextAddonsTotal) * 100) / 100;
    const priceField = editingService.discounted_amount != null
      ? { discounted_amount: nextTotal }
      : { amount: nextTotal };

    setEditingService({
      ...editingService,
      ...priceField,
      addons: nextAddons,
      metadata: { ...meta, billing_interval_months: newMonths },
    });
    setOpenProp(null);
  };

  /**
   * Somma degli add-on scelti riportata a un altro periodo, per l'anteprima del
   * selettore di frequenza. Usa lo stesso calcolo del cambio vero, altrimenti
   * il prezzo mostrato e quello applicato non coinciderebbero.
   */
  const addonsForPeriod = (months: number): number => {
    if (!editingService) return 0;
    const cur = billingMonthsOf(editingService) || 1;
    const stype = serviceTypeOf(editingService);
    const total = (editingService.addons ?? [])
      .filter((a) => !a.is_included_in_base)
      .reduce((s, sel) => s + addonAmountForPeriod(sel, cur, months, stype), 0);
    return Math.round(total * 100) / 100;
  };

  /** Gli stessi add-on a prezzo pieno: serve alla pillola "−N% listino". */
  const addonsFullForPeriod = (months: number): number => {
    if (!editingService) return 0;
    const cur = billingMonthsOf(editingService) || 1;
    const stype = serviceTypeOf(editingService);
    const total = (editingService.addons ?? [])
      .filter((a) => !a.is_included_in_base)
      .reduce((s, sel) => s + addonAmountForPeriod(sel, cur, months, stype, true), 0);
    return Math.round(total * 100) / 100;
  };

  /** Etichetta della durata: i mesi restano nel DB, gli anni si leggono meglio. */
  const durationLabel = (months?: number | null): string => {
    if (!months) return '';
    if (months % 12 === 0) {
      const years = months / 12;
      return `${months} mesi · ${years} ${years === 1 ? 'anno' : 'anni'}`;
    }
    return `${months} mesi`;
  };

  const money = (n: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number.isFinite(n) ? n : 0);

  /** Apre la descrizione a tutta pagina, montando prima l'editor se serve. */
  const openDescriptionSheet = () => {
    if (descOpen) {
      descEditorRef.current?.openFull();
      return;
    }
    setDescOpen(true);
    // Un giro di render perché DealServiceEditor esista e il ref sia popolato.
    setTimeout(() => descEditorRef.current?.openFull(), 0);
  };

  // Mobile view detection
  const isMobile = window.innerWidth < 768;
  
  return (
    <div className="flex flex-col h-full">
      <div className={`flex flex-col ${isMobile ? '' : 'md:flex-row'} h-full gap-0 relative`}>
        {/* Left sidebar with services */}
        <div className={`relative transition-all duration-300 ${
          externalSelectedServiceIndex !== null && isMobile ? 'hidden' :
          externalSelectedServiceIndex !== null && sidebarCollapsed ? 'md:w-16' :
          externalSelectedServiceIndex !== null ? 'md:w-[30%]' :
          'md:w-full'
        }`}>
          {/* Toggle button for sidebar collapse - positioned OUTSIDE the glass-card */}
          {externalSelectedServiceIndex !== null && !isMobile && (
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="absolute top-1/2 -right-3 transform -translate-y-1/2 z-30 p-1.5 bg-white dark:bg-gray-800 border border-black/10 dark:border-white/20 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={sidebarCollapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
            >
              {sidebarCollapsed ? <ChevronRight size={16} className="text-black dark:text-white" /> : <ChevronLeft size={16} className="text-black dark:text-white" />}
            </button>
          )}
          <div className="overflow-hidden flex flex-col h-full bg-white dark:bg-gray-900 border-r border-black/[0.06] dark:border-white/[0.08]">

          {/* Client name header */}
          {clientName && !sidebarCollapsed && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800/30">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#0f6cbd] flex items-center justify-center text-white text-sm font-medium">
                  {clientName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{clientName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Cliente</p>
                </div>
              </div>
            </div>
          )}

          <div className="p-3 border-b border-black/[0.06] dark:border-white/10">
            {/* VIVA: tab switcher Servizi / Prodotti magazzino — Notion-like underline.
                Terza scheda "Aggiunti" allineata a destra: sostituisce il
                carrellino in alto, mostra l'elenco compatto di tutto cio' che
                e' stato gia' aggiunto all'offerta. */}
            {!sidebarCollapsed && (
              <div className="flex items-center gap-1 -mx-3 px-3 -mt-3 pt-2 mb-2 border-b border-black/[0.06] dark:border-white/10">
                <button
                  type="button"
                  onClick={() => { setListMode('service'); setSearchTerm(''); }}
                  className={`px-2.5 py-2 text-[13px] font-medium transition-colors relative flex items-center gap-1.5 ${
                    listMode === 'service'
                      ? 'text-[#37352F] dark:text-white'
                      : 'text-black/50 dark:text-white/50 hover:text-black/80 dark:hover:text-white/80'
                  }`}
                >
                  <FontAwesomeIcon icon={faCube} className="w-3.5 h-3.5" />
                  Servizi
                  {listMode === 'service' && (
                    <div className="absolute -bottom-px left-0 right-0 h-[2px] bg-[#E91E8C] dark:bg-[#E91E8C]" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setListMode('product'); setSearchTerm(''); setCategoryFilter(null); }}
                  className={`px-2.5 py-2 text-[13px] font-medium transition-colors relative flex items-center gap-1.5 ${
                    listMode === 'product'
                      ? 'text-[#37352F] dark:text-white'
                      : 'text-black/50 dark:text-white/50 hover:text-black/80 dark:hover:text-white/80'
                  }`}
                >
                  <Package className="w-3.5 h-3.5" />
                  Prodotti
                  {listMode === 'product' && (
                    <div className="absolute -bottom-px left-0 right-0 h-[2px] bg-[#E91E8C] dark:bg-[#E91E8C]" />
                  )}
                </button>

                {/* Terza scheda — Aggiunti — tutta a destra */}
                {selectedServices.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setListMode('added'); setSearchTerm(''); }}
                    className={`ml-auto px-2.5 py-2 text-[13px] font-medium transition-colors relative flex items-center gap-1.5 ${
                      listMode === 'added'
                        ? 'text-[#37352F] dark:text-white'
                        : 'text-black/50 dark:text-white/50 hover:text-black/80 dark:hover:text-white/80'
                    }`}
                    title="Vedi e modifica gli elementi gia' aggiunti all'offerta"
                  >
                    <ShoppingCart className="w-3.5 h-3.5" />
                    {selectedServices.length} {selectedServices.length === 1 ? 'aggiunto' : 'aggiunti'}
                    {listMode === 'added' && (
                      <div className="absolute -bottom-px left-0 right-0 h-[2px] bg-[#E91E8C] dark:bg-[#E91E8C]" />
                    )}
                  </button>
                )}
              </div>
            )}

            <div className={`flex ${sidebarCollapsed ? 'justify-center' : 'justify-between'} items-center mb-2`}>
              {!sidebarCollapsed && (
                <p className="text-[11px] uppercase tracking-wide font-medium text-black/50 dark:text-white/50">
                  {listMode === 'product'
                    ? (productsTotal != null && productsTotal > products.length
                        ? `Articoli (${products.length} di ${productsTotal})`
                        : `Articoli (${products.length})`)
                    : externalSelectedServiceIndex !== null
                      ? `Servizi (${selectedServices.length})`
                      : (t.selectServiceToStart || 'Seleziona un servizio per iniziare')}
                </p>
              )}
              {/* Bottone "+" servizio personalizzato — visibile solo nel tab Servizi */}
              {listMode === 'service' && (
              <button
                className="w-6 h-6 flex items-center justify-center bg-black/[0.04] dark:bg-white/[0.08] text-black/65 dark:text-white/65 hover:bg-black/[0.08] dark:hover:bg-white/[0.12] hover:text-black dark:hover:text-white rounded-md transition-colors"
                onClick={() => {
                  // Creiamo un servizio personalizzato vuoto
                  const customService: Service = {
                    id: `custom-${Date.now()}`,
                    title: t.customService || 'Servizio personalizzato',
                    short_description: '',
                    description: '',
                    list_price: null,
                    service_type: 'one_time',
                    organization_id: '',
                    is_private: false,
                    created_at: new Date().toISOString(),
                    variables: null,
                    category_id: undefined,
                    category: undefined
                  };
                  handleSelectService(customService);
                }}
                title={sidebarCollapsed ? (t.addCustomService || 'Servizio personalizzato') : undefined}
                aria-label={t.addCustomService || 'Servizio personalizzato'}
              >
                <span className="font-bold text-lg">+</span>
              </button>
              )}
            </div>
            {!sidebarCollapsed && (
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-black/35 dark:text-white/35 pointer-events-none" />
                <input
                  type="text"
                  className="w-full pl-8 pr-7 py-1.5 text-[13px] bg-white dark:bg-white/[0.03] border border-black/[0.08] dark:border-white/[0.1] focus:border-black/20 dark:focus:border-white/25 rounded-md text-black dark:text-white placeholder:text-black/35 dark:placeholder:text-white/40 outline-none transition-colors"
                  placeholder={listMode === 'product' ? 'Cerca per nome o SKU…' : (t.searchServices || 'Cerca servizi…')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="absolute right-1.5 top-1/2 transform -translate-y-1/2 p-0.5 rounded hover:bg-black/[0.05] dark:hover:bg-white/[0.06] text-black/40 hover:text-black dark:text-white/40 dark:hover:text-white"
                    onClick={() => setSearchTerm('')}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            )}

            {listMode === 'product' && !sidebarCollapsed && (
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyInStock}
                  onChange={(e) => setOnlyInStock(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-[12px] text-black/65 dark:text-white/65">Solo articoli con giacenza</span>
              </label>
            )}

            {listMode === 'service' && categories.length > 0 && !sidebarCollapsed && (
              <div className="mb-2 relative">
                {categoriesCanScrollLeft && (
                  <button
                    type="button"
                    onClick={() => scrollCategoriesBy(-160)}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-6 w-6 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 border border-black/[0.08] dark:border-white/15 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-black/65 dark:text-white/65 transition-colors"
                    aria-label="Scorri categorie a sinistra"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                )}
                {categoriesCanScrollRight && (
                  <button
                    type="button"
                    onClick={() => scrollCategoriesBy(160)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-6 w-6 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 border border-black/[0.08] dark:border-white/15 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-black/65 dark:text-white/65 transition-colors"
                    aria-label="Scorri categorie a destra"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                )}
                <div
                  ref={categoriesScrollRef}
                  onScroll={updateCategoriesScrollFlags}
                  className="flex overflow-x-auto gap-1.5 scrollbar-hide pb-2 scroll-smooth"
                  style={{
                    paddingLeft: categoriesCanScrollLeft ? '28px' : undefined,
                    paddingRight: categoriesCanScrollRight ? '28px' : undefined,
                  }}
                >
                  <button
                    className={`flex-shrink-0 px-2 py-0.5 text-xs rounded-full ${
                      categoryFilter === null
                        ? (isDark ? 'bg-white/20 text-white font-medium' : 'bg-black/20 text-black font-medium')
                        : (isDark ? 'bg-white/5 text-white/70 hover:bg-white/10' : 'bg-black/5 text-black/70 hover:bg-black/10')
                    }`}
                    onClick={() => setCategoryFilter(null)}
                  >
                    {t.allCategories || 'Tutte'}
                  </button>

                  {categories.map(category => (
                    <button
                      key={category.id}
                      className={`flex-shrink-0 px-2 py-0.5 text-xs rounded-full transition-colors`}
                      style={
                        categoryFilter === category.id
                          ? { backgroundColor: `${category.color || '#3B82F6'}30`, color: isDark ? '#ffffff' : '#000000', fontWeight: 500 }
                          : { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }
                      }
                      onClick={() => setCategoryFilter(
                        categoryFilter === category.id ? null : category.id
                      )}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div ref={serviceListRef} onScroll={handleListScroll} className="flex-1 overflow-y-auto p-2">
            {/* Lista "Aggiunti" — mostra tutti i servizi/prodotti gia' nel
                deal. Click su una riga apre l'editor del relativo elemento.
                X compatto rimuove (con conferma). */}
            {listMode === 'added' ? (
              selectedServices.length === 0 ? (
                <div className="text-center py-10 px-4 flex flex-col items-center justify-center gap-2">
                  <ShoppingCart size={20} className="text-black/35 dark:text-white/35" />
                  <p className="text-[13px] text-black/55 dark:text-white/55">
                    Nessun elemento aggiunto
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {selectedServices.map((row, idx) => {
                    const isProduct = (row as any).line_type === 'product';
                    const isCustom = row.service_id?.toString().startsWith('custom-');
                    let name = '';
                    let typeLabel = '';
                    if (isProduct) {
                      // Dalla cache, non da `products`: quello e' solo la pagina
                      // corrente del catalogo e potrebbe non contenerlo.
                      const p = productsCacheRef.current.get((row as any).product_id);
                      name = p?.name || (row as any).custom_service_name || 'Prodotto';
                      typeLabel = 'Prodotto';
                    } else if (isCustom) {
                      name = row.custom_service_name || 'Servizio personalizzato';
                      typeLabel = 'Custom';
                    } else {
                      const svc = services.find(s => s.id === row.service_id);
                      name = svc?.title || 'Servizio';
                      typeLabel = svc?.service_type === 'monthly'
                        ? 'Mensile'
                        : svc?.service_type === 'annual'
                          ? 'Annuale'
                          : 'Una tantum';
                    }
                    const amount = row.discounted_amount ?? row.amount ?? 0;
                    const isActive = externalSelectedServiceIndex === idx;
                    return (
                      <button
                        type="button"
                        key={`${row.service_id}-${idx}`}
                        onClick={() => onExternalServiceIndexChange(idx)}
                        className={`group text-left w-full p-3 rounded-lg border transition-all flex items-center gap-3 ${
                          isActive
                            ? 'border-blue-200 dark:border-blue-700/50 bg-blue-50/60 dark:bg-blue-900/15'
                            : 'border-black/[0.06] dark:border-white/[0.08] bg-white dark:bg-gray-800/40 hover:border-black/[0.12] dark:hover:border-white/[0.18]'
                        }`}
                      >
                        <div className="flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center text-[11px] font-semibold bg-black/[0.04] dark:bg-white/[0.06] text-black/65 dark:text-white/65">
                          {isProduct ? <Package className="h-4 w-4" /> : <FontAwesomeIcon icon={faCube} className="w-3.5 h-3.5" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-medium text-black dark:text-white truncate">{name}</div>
                          <div className="text-[11px] text-black/50 dark:text-white/50">{typeLabel}</div>
                        </div>
                        <div className="text-[13px] font-semibold text-black dark:text-white whitespace-nowrap">
                          €{amount.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={async (e) => {
                            e.stopPropagation();
                            const ok = await confirm({
                              type: 'danger',
                              title: isProduct ? 'Rimuovi prodotto' : 'Rimuovi servizio',
                              message: `Sei sicuro di voler rimuovere "${name}" dall'offerta?`,
                              confirmText: 'Rimuovi',
                              cancelText: 'Annulla',
                            });
                            if (ok) {
                              handleRemoveService(idx);
                              toast.success(`${isProduct ? 'Prodotto' : 'Servizio'} "${name}" rimosso`);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              (e.currentTarget as HTMLElement).click();
                            }
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded text-black/45 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                          title="Rimuovi"
                        >
                          <X size={14} />
                        </span>
                      </button>
                    );
                  })}
                </div>
              )
            ) : listMode === 'product' ? (
              products.length === 0 ? (
                <div className="text-center py-10 px-4 flex flex-col items-center justify-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
                    <Package size={18} className="text-black/40 dark:text-white/40" />
                  </div>
                  <div className="text-[13px] font-medium text-black/70 dark:text-white/70">
                    {productsLoading
                      ? 'Caricamento articoli…'
                      : productsError
                        ? 'Articoli non disponibili'
                        : 'Nessun articolo trovato'}
                  </div>
                  <p className="text-[12px] text-black/45 dark:text-white/45 max-w-[260px]">
                    {productsLoading
                      ? 'Sto caricando gli articoli attivi.'
                      : productsError
                        ? `${productsError} Riprova tra un istante.`
                        : onlyInStock
                          ? 'Nessun articolo con giacenza maggiore di zero. Togli la spunta per vederli tutti.'
                          : searchTerm
                            ? 'Prova a cercare per nome o codice SKU diverso.'
                            : 'Aggiungi articoli dal modulo Articoli per poterli vendere nelle offerte.'}
                  </p>
                  {!productsLoading && !productsError && onlyInStock && (
                    <button
                      className="px-3 py-1.5 text-[12px] font-medium bg-black/[0.04] hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/10 text-black dark:text-white rounded-md transition-colors"
                      onClick={() => setOnlyInStock(false)}
                    >
                      Mostra tutti gli articoli
                    </button>
                  )}
                  {!productsLoading && productsError && (
                    <button
                      className="px-3 py-1.5 text-[12px] font-medium bg-black/[0.04] hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/10 text-black dark:text-white rounded-md transition-colors"
                      onClick={() => caricaProdotti(0, debouncedProductSearch, onlyInStock)}
                    >
                      Riprova
                    </button>
                  )}
                  {!productsLoading && !productsError && searchTerm && (
                    <button
                      className="px-3 py-1.5 text-[12px] font-medium bg-black/[0.04] hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/10 text-black dark:text-white rounded-md transition-colors"
                      onClick={() => setSearchTerm('')}
                    >
                      Azzera ricerca
                    </button>
                  )}
                </div>
              ) : (() => {
                // Split: prodotti già nel deal vs catalogo magazzino
                const inCart = products.filter(p => (selectedServices ?? []).some(s => s.line_type === 'product' && s.product_id === p.id));
                const inCatalog = products.filter(p => !inCart.some(c => c.id === p.id));

                const renderProductCard = (product: ProductItem) => {
                  const occurrencesInDeal = (selectedServices ?? []).filter(s => s.line_type === 'product' && s.product_id === product.id).length;
                  const lowStock = product.stock_quantity != null && Number(product.stock_quantity) <= 0;
                  // UX FIX coerente con i servizi: card prodotto già nel deal apre editing della riga esistente
                  const firstIndexInCart = occurrencesInDeal > 0
                    ? (selectedServices ?? []).findIndex(s => s.line_type === 'product' && s.product_id === product.id)
                    : -1;
                  return (
                    <button
                      type="button"
                      key={product.id}
                      onClick={() => {
                        if (firstIndexInCart >= 0) {
                          onExternalServiceIndexChange(firstIndexInCart);
                        } else {
                          handleSelectProduct(product);
                        }
                      }}
                      className={`group text-left w-full p-3 rounded-lg border transition-all ${
                        occurrencesInDeal > 0
                          ? 'border-blue-200 dark:border-blue-700/50 bg-blue-50/60 dark:bg-blue-900/15 hover:bg-blue-50 dark:hover:bg-blue-900/25 shadow-sm'
                          : 'border-black/[0.06] dark:border-white/[0.08] bg-white dark:bg-gray-800/40 hover:border-black/[0.12] dark:hover:border-white/[0.18] hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Thumbnail prodotto: foto se disponibile, altrimenti icona Package */}
                        <div className="flex-shrink-0 w-12 h-12 rounded-md overflow-hidden bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
                          {product.main_image_url ? (
                            <img
                              src={product.main_image_url}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <Package className="h-5 w-5 text-black/40 dark:text-white/40" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <h4 className="text-[13px] font-medium text-black dark:text-white truncate">{product.name}</h4>
                                {occurrencesInDeal > 0 && (
                                  <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-1 py-px rounded">
                                    {occurrencesInDeal > 1 ? `${occurrencesInDeal}×` : '✓'}
                                  </span>
                                )}
                              </div>
                              {product.sku && (
                                <p className="text-[11px] text-black/50 dark:text-white/50 line-clamp-1 mt-0.5">SKU: {product.sku}</p>
                              )}
                              <div className="flex flex-wrap gap-1 mt-1 items-center">
                                <span className="text-[10px] text-black/45 dark:text-white/50">Prodotto</span>
                                {product.stock_quantity != null && (
                                  <>
                                    <span className="text-[10px] text-black/30 dark:text-white/30">·</span>
                                    <span className={`text-[10px] ${lowStock ? 'text-red-600 dark:text-red-400 font-medium' : 'text-black/45 dark:text-white/50'}`}>
                                      Stock: {formatQuantity(product.stock_quantity, { unit: resolveProductUnit(product), unitDisplay: 'none' })}{lowStock ? ' ⚠' : ''}
                                    </span>
                                  </>
                                )}
                                <CommittedAvailable
                                  committed={product.committed_quantity}
                                  available={product.available_quantity}
                                  unit={resolveProductUnit(product)}
                                  variant="compact"
                                />
                              </div>
                            </div>
                            <div className="text-[13px] font-semibold text-black dark:text-white whitespace-nowrap">
                              {product.regular_price != null ? `€${Number(product.regular_price).toFixed(2)}` : '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                };

                return (
                  <div className="flex flex-col gap-3">
                    {inCart.length > 0 && (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between px-2.5 py-1">
                          <span className="text-[10px] uppercase tracking-wide font-medium text-black/50 dark:text-white/50">
                            Inseriti in offerta ({inCart.length})
                          </span>
                        </div>
                        {inCart.map(renderProductCard)}
                      </div>
                    )}
                    {inCatalog.length > 0 && (
                      <div className="flex flex-col gap-1">
                        {inCart.length > 0 && (
                          <div className="flex items-center px-2.5 py-1 mt-1">
                            <span className="text-[10px] uppercase tracking-wide font-medium text-black/50 dark:text-white/50">
                              Articoli ({inCatalog.length})
                            </span>
                            <div className="flex-1 ml-2 h-px bg-black/[0.06] dark:bg-white/10" />
                          </div>
                        )}
                        {inCatalog.map(renderProductCard)}
                      </div>
                    )}
                    {/* Fondo lista: lo scroll carica da solo, il bottone e' la
                        via esplicita quando la lista non scrolla abbastanza. */}
                    {productsLoadingMore ? (
                      <div className="py-3 text-center text-[12px] text-black/45 dark:text-white/45">
                        Caricamento…
                      </div>
                    ) : productsHasMore ? (
                      <button
                        type="button"
                        onClick={caricaAltriProdotti}
                        className="mx-auto my-2 px-3 py-1.5 text-[12px] font-medium bg-black/[0.04] hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/10 text-black dark:text-white rounded-md transition-colors"
                      >
                        Carica altri articoli
                      </button>
                    ) : null}
                  </div>
                );
              })()
            ) : filteredServices.length === 0 ? (
              <div className="text-center py-10 px-4 flex flex-col items-center justify-center gap-3">
                <div className="w-10 h-10 rounded-full bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
                  <Settings size={18} className="text-black/40 dark:text-white/40" />
                </div>
                <div className="text-[13px] font-medium text-black/70 dark:text-white/70">
                  {searchTerm || categoryFilter !== null
                    ? (t.noServicesFound || 'Nessun servizio trovato')
                    : (t.noServicesAvailable || 'Nessun servizio disponibile')}
                </div>
                <p className="text-[12px] text-black/45 dark:text-white/45 max-w-[260px]">
                  {searchTerm || categoryFilter !== null
                    ? 'Prova a modificare i filtri o azzerali per vedere tutti i servizi.'
                    : 'Crea un servizio personalizzato per questa offerta, oppure aggiungi servizi al catalogo dalle Impostazioni.'}
                </p>
                {searchTerm || categoryFilter !== null ? (
                  <button
                    className="px-3 py-1.5 text-[12px] font-medium bg-black/[0.04] hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/10 text-black dark:text-white rounded-md transition-colors"
                    onClick={() => {
                      setSearchTerm('');
                      setCategoryFilter(null);
                    }}
                  >
                    {t.clearFilters || 'Azzera filtri'}
                  </button>
                ) : (
                  <button
                    className="px-3 py-1.5 text-[12px] font-medium bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-md transition-colors"
                    onClick={() => {
                      // Creiamo un servizio personalizzato vuoto
                      const customService: Service = {
                        id: `custom-${Date.now()}`,
                        title: t.customService || 'Servizio personalizzato',
                        short_description: '',
                        description: '',
                        list_price: null,
                        service_type: 'one_time',
                        organization_id: '',
                        is_private: false,
                        created_at: new Date().toISOString(),
                        variables: null,
                        category_id: undefined,
                        category: undefined
                      };
                      handleSelectService(customService);
                    }}
                  >
                    {t.createCustomService || 'Crea servizio personalizzato'}
                  </button>
                )}
              </div>
            ) : (
              (() => {
                // Split: i servizi nel CARRELLO (già aggiunti) vs il resto del CATALOGO.
                // In edit mode (service selected): mostriamo carrello prima e catalogo dopo
                // separati da un header chiaro. Su tutta-larghezza (selezione iniziale)
                // ha senso anche, riduce lo scroll per gli utenti che hanno già aggiunto qualcosa.
                const inCart = filteredServices.filter(s => selectedServices.some(ss => ss.service_id === s.id));
                const inCatalog = filteredServices.filter(s => !selectedServices.some(ss => ss.service_id === s.id));

                const renderCard = (service: Service) => {
                  const isSelected = selectedServices.some(ss => ss.service_id === service.id);
                  const selectedCount = selectedServices.filter(ss => ss.service_id === service.id).length;
                  const categoryColor = service.category?.color || '#3B82F6';
                  // Iniziali per l'avatar generato dal titolo del servizio (max 2 char)
                  const initials = service.title
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map(w => w[0]?.toUpperCase())
                    .join('');

                  // Indici e dati delle istanze duplicate del servizio (se >1)
                  const instances = isSelected
                    ? selectedServices
                        .map((ss, idx) => ({ row: ss, idx }))
                        .filter(({ row }) => row.service_id === service.id)
                    : [];
                  // Index del primo match: click sulla card master apre questo
                  const firstIdx = instances.length > 0 ? instances[0].idx : -1;

                  const cardInner = (
                    <div className="flex items-start gap-3">
                      <div
                        className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center text-[12px] font-semibold"
                        style={{
                          backgroundColor: `${categoryColor}1a`,
                          color: categoryColor,
                        }}
                      >
                        {initials || '·'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-[13px] font-medium text-black dark:text-white truncate">{service.title}</h4>
                              {isSelected && (
                                <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-1 py-px rounded">
                                  {selectedCount > 1 ? `${selectedCount}×` : '✓'}
                                </span>
                              )}
                            </div>
                            {service.short_description && (
                              <p className="text-[11px] text-black/55 dark:text-white/55 line-clamp-2 mt-0.5">{service.short_description}</p>
                            )}
                            <div className="flex flex-wrap gap-1 mt-1.5 items-center">
                              <span className="text-[10px] text-black/45 dark:text-white/50">
                                {service.service_type === 'monthly'
                                  ? (t.monthlyService || 'Mensile')
                                  : service.service_type === 'annual'
                                  ? (t.annualService || 'Annuale')
                                  : (t.oneTimeService || 'Una tantum')}
                              </span>
                              {service.category && (
                                <>
                                  <span className="text-[10px] text-black/30 dark:text-white/30">·</span>
                                  <span
                                    className="text-[10px] font-medium"
                                    style={{ color: categoryColor }}
                                  >
                                    {service.category.name}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="text-[13px] font-semibold text-black dark:text-white whitespace-nowrap">
                            €{service.list_price}
                            {service.service_type === 'monthly' && <span className="text-[10px] font-normal text-black/40 dark:text-white/40">/m</span>}
                            {service.service_type === 'annual' && <span className="text-[10px] font-normal text-black/40 dark:text-white/40">/a</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );

                  return (
                    <div key={`${service.id}-card`} className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          // Se il servizio e' gia' nel carrello, apri la PRIMA
                          // istanza (l'utente puo' duplicarla con "Aggiungi
                          // un altro" sotto). Altrimenti, prima volta: aggiungi.
                          if (isSelected && firstIdx >= 0) {
                            onExternalServiceIndexChange(firstIdx);
                          } else {
                            handleSelectService(service);
                          }
                        }}
                        className={`group text-left w-full p-3 rounded-lg border transition-all ${
                          isSelected
                            ? 'border-blue-200 dark:border-blue-700/50 bg-blue-50/60 dark:bg-blue-900/15 hover:bg-blue-50 dark:hover:bg-blue-900/25 shadow-sm'
                            : 'border-black/[0.06] dark:border-white/[0.08] bg-white dark:bg-gray-800/40 hover:border-black/[0.12] dark:hover:border-white/[0.18] hover:shadow-sm'
                        }`}
                      >
                        {cardInner}
                      </button>

                      {/* Lista delle istanze duplicate (>1) cliccabili. La
                          prima istanza appare comunque come riga sotto la card
                          per simmetria/chiarezza quando ce ne sono piu'. */}
                      {isSelected && instances.length > 1 && (
                        <div className="ml-3 pl-3 border-l border-blue-200 dark:border-blue-800/40 flex flex-col gap-0.5 mt-0.5">
                          {instances.map(({ row, idx }, i) => {
                            const instanceName = row.custom_service_name?.trim() || `${service.title} #${i + 1}`;
                            const isActive = externalSelectedServiceIndex === idx;
                            return (
                              <button
                                type="button"
                                key={`${service.id}-inst-${idx}`}
                                onClick={() => onExternalServiceIndexChange(idx)}
                                className={`text-left w-full px-2 py-1 rounded text-[12px] flex items-center justify-between gap-2 transition-colors ${
                                  isActive
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-black dark:text-white font-medium'
                                    : 'text-black/70 dark:text-white/70 hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                                }`}
                              >
                                <span className="truncate">{instanceName}</span>
                                <span className="text-[11px] text-black/45 dark:text-white/50 whitespace-nowrap">
                                  €{((row.discounted_amount ?? row.amount) || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Link "Aggiungi un altro" sotto la card — solo quando
                          il servizio e' gia' nel carrello (almeno 1 istanza). */}
                      {isSelected && (
                        <button
                          type="button"
                          onClick={() => handleSelectService(service)}
                          className="self-start ml-3 inline-flex items-center gap-1 text-[11px] text-black/50 dark:text-white/50 hover:text-[#E91E8C] px-1.5 py-0.5 rounded transition-colors"
                          title="Aggiungi un'altra istanza di questo servizio"
                        >
                          <span className="text-[12px] leading-none">+</span>
                          <span>Aggiungi un altro</span>
                        </button>
                      )}
                    </div>
                  );
                };

                return (
                  <div className="flex flex-col gap-3">
                    {/* SEZIONE 1 — Servizi nel carrello (se ce ne sono) */}
                    {inCart.length > 0 && (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between px-2.5 py-1">
                          <span className="text-[10px] uppercase tracking-wide font-medium text-black/50 dark:text-white/50">
                            Inseriti in offerta ({selectedServices.length})
                          </span>
                        </div>
                        {inCart.map(renderCard)}
                      </div>
                    )}

                    {/* SEZIONE 2 — Catalogo (servizi non ancora aggiunti) */}
                    {inCatalog.length > 0 && (
                      <div className="flex flex-col gap-1">
                        {inCart.length > 0 && (
                          <div className="flex items-center px-2.5 py-1 mt-1">
                            <span className="text-[10px] uppercase tracking-wide font-medium text-black/50 dark:text-white/50">
                              {t.catalog || 'Catalogo'} ({inCatalog.length})
                            </span>
                            <div className="flex-1 ml-2 h-px bg-black/[0.06] dark:bg-white/10" />
                          </div>
                        )}
                        {inCatalog.map(renderCard)}
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </div>
          </div>
        </div>

        {/* Right side with service details — sfondo beige chiaro coerente
            con il pannello destro dello Step 1 (DealStep1OfferDetailsPanel) */}
        {externalSelectedServiceIndex !== null && editingService && (
          <div
            className={`overflow-hidden flex flex-col transition-all duration-300 bg-[#F7F6F3] dark:bg-gray-900/60 ${
              isMobile ? 'flex-1' : sidebarCollapsed ? 'md:w-[calc(100%-4rem)]' : 'md:w-[70%]'
            }`}
            style={{}}
          >
            {/* Header compatto in UNA riga: nome servizio + tabs inline +
                azioni (rimuovi, chiudi). Niente badge tipo grande, niente
                titolo a riga 18px: la fonte del tipo e' gia' chiara dalla
                sidebar a sinistra che mostra il servizio selezionato. */}
            <div className="px-5 py-2 border-b border-black/[0.06] dark:border-white/10 flex items-center gap-3 bg-[#F7F6F3] dark:bg-gray-900/60">
              {(() => {
                const isProduct = editingService.line_type === 'product';
                const isCustom = editingService.service_id?.startsWith('custom-');
                // Nome originale del catalogo (fallback per il placeholder)
                let originalName = '';
                if (isProduct) {
                  const p = products.find(pp => pp.id === editingService.product_id);
                  originalName = p?.name || 'Prodotto';
                } else if (isCustom) {
                  originalName = t.customService || 'Servizio personalizzato';
                } else {
                  const svc = services.find(s => s.id === editingService.service_id);
                  originalName = svc?.title || (t.editService || 'Servizio');
                }
                // Nome corrente = custom_service_name (rinomina utente) || originalName
                const currentName = editingService.custom_service_name?.trim() || originalName;
                return (
                  // Wrapper "group" per mostrare l'icona penna in hover; il
                  // gruppo si attiva sia in hover sull'area che in focus dell'input.
                  <div className="group inline-flex items-center gap-1.5 min-w-0 flex-shrink">
                    <input
                      type="text"
                      value={editingService.custom_service_name ?? ''}
                      placeholder={originalName}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEditingService({ ...editingService, custom_service_name: v });
                        if (isCustom) setCustomServiceName(v);
                      }}
                      className="text-[14px] font-semibold text-black dark:text-white truncate min-w-0 flex-shrink bg-transparent border-0 outline-none focus:bg-white dark:focus:bg-white/[0.05] focus:ring-0 px-1 -mx-1 rounded transition-colors hover:bg-white/60 dark:hover:bg-white/[0.04]"
                      title={`Rinomina (originale: ${originalName})`}
                      style={{ width: `${Math.max(currentName.length, originalName.length, 8) + 2}ch`, maxWidth: '40ch' }}
                    />
                    {/* Icona penna: sempre visibile sottile, piu' marcata in
                        hover/focus. Indica che il titolo e' editabile. */}
                    <Pencil
                      className="h-3 w-3 flex-shrink-0 text-black/30 dark:text-white/30 group-hover:text-black/60 dark:group-hover:text-white/60 group-focus-within:text-[#E91E8C] transition-colors"
                      aria-hidden="true"
                    />
                  </div>
                );
              })()}

              {/* Tabs inline — Notion-like, sottili.
                  Per riga prodotto (line_type='product') mostriamo SOLO la tab
                  principale rinominata "Prodotto" e nascondiamo "Variabili e
                  costi" che si applica solo ai servizi (template variabili,
                  costi servizio, ecc. non hanno senso per un articolo magazzino). */}
              <nav className="flex items-center gap-0.5 ml-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setActiveTab('details')}
                  className={`px-2 py-1 text-[12px] font-medium transition-colors relative flex items-center gap-1 rounded ${
                    activeTab === 'details'
                      ? 'text-[#37352F] dark:text-white bg-white dark:bg-white/[0.08]'
                      : 'text-black/50 dark:text-white/50 hover:text-black/80 dark:hover:text-white/80 hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
                  }`}
                >
                  <FontAwesomeIcon icon={editingService.line_type === 'product' ? faCube : faCube} className="w-3 h-3" />
                  {editingService.line_type === 'product' ? 'Prodotto' : 'Servizio'}
                </button>
                {editingService.line_type !== 'product' && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('variables')}
                    className={`px-2 py-1 text-[12px] font-medium transition-colors relative flex items-center gap-1 rounded ${
                      activeTab === 'variables'
                        ? 'text-[#37352F] dark:text-white bg-white dark:bg-white/[0.08]'
                        : 'text-black/50 dark:text-white/50 hover:text-black/80 dark:hover:text-white/80 hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
                    }`}
                  >
                    <FontAwesomeIcon icon={faDollarSign} className="w-3 h-3" />
                    Variabili e costi
                  </button>
                )}
              </nav>

              {/* Spaziatore */}
              <div className="flex-1" />

              {/* Azione Rimuovi: rosso esplicito + popover di conferma sotto.
                  Click apre il fumetto con tasto rosso "Rimuovi" e "Annulla". */}
              {externalSelectedServiceIndex !== null && externalSelectedServiceIndex >= 0 && (() => {
                const isProduct = editingService?.line_type === 'product';
                let displayName = isProduct ? 'Prodotto' : 'Servizio';
                if (isProduct) {
                  const p = products.find(pp => pp.id === editingService?.product_id);
                  displayName = p?.name || (editingService as any)?.custom_service_name || 'Prodotto';
                } else if (editingService?.service_id?.startsWith('custom-')) {
                  displayName = editingService?.custom_service_name || 'Servizio personalizzato';
                } else {
                  const svc = services.find(s => s.id === editingService?.service_id);
                  displayName = (editingService?.custom_service_name?.trim() || svc?.title) || 'Servizio';
                }
                return (
                  <div className="relative flex-shrink-0" ref={removePopoverRef}>
                    <button
                      type="button"
                      onClick={() => setRemoveConfirmOpen(v => !v)}
                      className="inline-flex items-center gap-1 text-[12px] font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded transition-colors"
                      title={isProduct ? 'Rimuovi prodotto' : 'Rimuovi servizio'}
                    >
                      <X size={13} />
                      <span>Rimuovi</span>
                    </button>
                    {removeConfirmOpen && (
                      <div
                        className="absolute right-0 top-full mt-1 z-30 w-72 bg-white dark:bg-gray-900 border border-black/10 dark:border-white/15 rounded-lg shadow-lg p-3 animate-[fadeIn_0.15s_ease-out]"
                        role="dialog"
                      >
                        <p className="text-[13px] text-black dark:text-white mb-2">
                          Rimuovere <span className="font-medium">{displayName}</span> dall'offerta?
                        </p>
                        <p className="text-[11px] text-black/50 dark:text-white/50 mb-3">
                          L'azione non puo' essere annullata.
                        </p>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setRemoveConfirmOpen(false)}
                            className="px-2.5 py-1 text-[12px] text-black/65 dark:text-white/65 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded transition-colors"
                          >
                            Annulla
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setRemoveConfirmOpen(false);
                              handleRemoveService(externalSelectedServiceIndex);
                              toast.success(`${isProduct ? 'Prodotto' : 'Servizio'} "${displayName}" rimosso`);
                            }}
                            className="px-2.5 py-1 text-[12px] font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                          >
                            Rimuovi
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* Chiudi pannello: bottone testuale esplicito per evitare
                  confusione con "Rimuovi". */}
              <button
                type="button"
                className="inline-flex items-center text-[12px] font-medium text-black/65 dark:text-white/65 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06] px-2 py-1 rounded transition-colors flex-shrink-0"
                onClick={() => onExternalServiceIndexChange(null)}
                aria-label="Chiudi"
                title="Chiudi"
              >
                Chiudi
              </button>
            </div>

            {/* py-4 spostato nel children: cosi' la toolbar sticky della
                descrizione (sticky top-0) aderisce davvero al top del
                container scrollabile, senza il gap di padding sopra. */}
            <div ref={editorScrollRef} className="flex-1 overflow-y-auto px-5">
              {activeTab === 'details' && (
                <div className="space-y-5 py-4">
                {/* === PREZZO: il titolo della riga === */}
                {(() => {
                  const isProductRow = editingService.line_type === 'product';
                  const isCustomRow = editingService.service_id.startsWith('custom-');
                  const stype = serviceTypeOf(editingService);
                  const months = billingMonthsOf(editingService);
                  const recurring = !isProductRow && stype !== 'one_time' && !!stype;
                  const base = baseAmountOf(editingService);
                  const addons = billableAddonsTotal(editingService);
                  const hasAddons = addons > 0;

                  return (
                    <>
                      {/* Nome del servizio personalizzato: senza, la riga non ha titolo */}
                      {isCustomRow && (
                        <input
                          type="text"
                          className="w-full px-2 py-1 -mx-2 bg-transparent border border-transparent rounded text-[15px] font-medium text-black dark:text-white hover:bg-black/[0.03] dark:hover:bg-white/[0.04] focus:bg-white dark:focus:bg-gray-800/60 focus:border-black/[0.15] dark:focus:border-white/20 focus:outline-none transition-colors"
                          value={customServiceName}
                          onChange={(e) => {
                            setCustomServiceName(e.target.value);
                            setEditingService({
                              ...editingService,
                              custom_service_name: e.target.value,
                              custom_title: e.target.value,
                            });
                          }}
                          placeholder={t.customServiceNamePlaceholder || 'Nome del servizio…'}
                        />
                      )}

                      {/* Categoria della riga scritta a mano: obbligatoria, altrimenti
                          questa vendita non compare nelle liste per servizio acquistato. */}
                      {isCustomRow && categorieServizio.length > 0 && (
                        <div className="mt-1 flex items-center gap-2">
                          <select
                            value={editingService.category_id ?? ''}
                            onChange={(e) =>
                              setEditingService({
                                ...editingService,
                                category_id: e.target.value || null,
                              })
                            }
                            className={`px-2 py-1 -ml-2 bg-transparent border rounded text-[13px] focus:outline-none transition-colors ${
                              editingService.category_id
                                ? 'border-transparent text-[#787774] dark:text-gray-400 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]'
                                : 'border-amber-400/70 text-amber-700 dark:text-amber-400'
                            }`}
                          >
                            <option value="">Categoria…</option>
                            {categorieServizio.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                          {!editingService.category_id && (
                            <span className="text-[11px] text-[#9B9A97] dark:text-gray-500">
                              Serve a ritrovare il cliente nelle liste per servizio acquistato
                            </span>
                          )}
                        </div>
                      )}

                      {/* Il titolo mostra il prezzo del SERVIZIO, add-on esclusi:
                          quello con gli add-on è il totale, e sta nel footer.
                          `amount` invece li include, quindi qui si scorporano e
                          si riaggiungono al salvataggio.

                          Eccezione: i servizi che tengono il prezzo negli add-on
                          (base 0 + piani) non avrebbero niente da scontare, e il
                          commerciale resterebbe senza leva sul prezzo. Lì il
                          titolo lavora direttamente sul totale della riga. */}
                      {(() => {
                        const priceOnTotal = base === 0 && hasAddons;
                        const offset = priceOnTotal ? 0 : addons;
                        return (
                      <PriceHeadline
                        amount={Math.max(0, Number(editingService.amount ?? 0) - offset)}
                        discountedAmount={
                          editingService.discounted_amount != null
                            ? Math.max(0, editingService.discounted_amount - offset)
                            : null
                        }
                        periodSuffix={recurring ? ` ${BILLING_PERIOD_SUFFIX[months] || '/mese'}` : ''}
                        zeroHint={priceOnTotal ? 'totale degli add-on, scontabile' : null}
                        onChange={({ amount, discounted_amount }) => {
                          const meta: any = editingService.metadata || {};
                          const nextAmount = Math.round((amount + offset) * 100) / 100;
                          // Su una riga prodotto il prezzo pieno resta il
                          // prodotto di quantita' x unitario: se il commerciale
                          // riscrive il listino qui, l'unitario lo segue,
                          // altrimenti le due cifre si contraddicono.
                          const qty = Number((editingService as any).quantity ?? 1) || 1;
                          const unitField = isProductRow && qty > 0
                            ? { unit_price: Math.round((nextAmount / qty) * 100) / 100 }
                            : {};
                          setEditingService({
                            ...editingService,
                            ...unitField,
                            amount: nextAmount,
                            discounted_amount:
                              discounted_amount != null
                                ? Math.round((discounted_amount + offset) * 100) / 100
                                : null,
                            // Il prezzo scritto a mano non si fa più sovrascrivere
                            // dal ricalcolo automatico al cambio di frequenza.
                            metadata: { ...meta, price_override: true },
                          } as any);
                        }}
                      />
                        );
                      })()}

                      {/* IVA sotto il prezzo: il prezzo è imponibile, qui l'importo IVA e il
                          totale della riga IVA inclusa (add-on compresi), con l'aliquota della
                          riga — o quella del cliente se applicata all'offerta. */}
                      {(() => {
                        const rate = Number(lockedVatRate ?? (editingService as any).vat_rate ?? 22);
                        const total = rowTotalOf(editingService);
                        const vat = Math.round(total * rate) / 100;
                        const eur = (n: number) => n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
                        const suffix = recurring ? ` ${BILLING_PERIOD_SUFFIX[months] || '/mese'}` : '';
                        return (
                          <div className="-mt-1 pb-1 text-[12px] tabular-nums text-black/50 dark:text-white/50">
                            + IVA {rate}% {eur(vat)}
                            <span className="mx-1">·</span>
                            <span className="font-medium text-black/75 dark:text-white/75">{eur(total + vat)}{suffix} IVA inclusa</span>
                            {hasAddons && <span> (add-on compresi)</span>}
                          </div>
                        );
                      })()}

                      {/* === PROPRIETÀ DELLA RIGA === */}
                      <div className="flex flex-col pt-1">
                        {/* Prodotti: unità, quantità e prezzo unitario compongono l'importo */}
                        {isProductRow && (() => {
                          const qty = Number((editingService as any).quantity ?? 1) || 1;
                          const unitPrice = Number((editingService as any).unit_price ?? editingService.amount ?? 0) || 0;
                          // Cambiando quantita' o prezzo unitario lo sconto va
                          // riscalato in proporzione. Ma da una base a zero non
                          // si ricava nessun rapporto: tenere il vecchio importo
                          // scontato (spesso 0, mentre si svuota il campo per
                          // riscriverlo) inchiodava la riga a "-100%" con il
                          // prezzo finale a 0,00 €. Senza rapporto, nessuno
                          // sconto.
                          const recalcWithDiscount = (newAmount: number) => {
                            const oldAmount = Number(editingService.amount ?? 0);
                            const oldDiscounted = editingService.discounted_amount;
                            if (oldDiscounted == null || oldAmount <= 0) return { amount: newAmount, discounted_amount: null };
                            const ratio = oldDiscounted / oldAmount;
                            return { amount: newAmount, discounted_amount: +(newAmount * ratio).toFixed(2) };
                          };
                          const inputCls = 'w-[150px] px-2 py-1 text-[13px] rounded border border-black/[0.12] dark:border-white/15 bg-white dark:bg-gray-800/60 text-black dark:text-white focus:border-[#2383E2] focus:outline-none focus:ring-2 focus:ring-[#2383E2]/20 transition-colors';
                          return (
                            <>
                              <PropertyRow
                                label="Unità di misura"
                                value={(editingService as any).unit_of_measure || orgDefaultUnit || DEFAULT_UNIT_OF_MEASURE}
                                action="cambia"
                                onClick={() => setOpenProp(openProp === 'uom' ? null : 'uom')}
                                expanded={openProp === 'uom'}
                              >
                                <div className="w-[220px]">
                                  <SearchableSelect
                                    value={(editingService as any).unit_of_measure || orgDefaultUnit || DEFAULT_UNIT_OF_MEASURE}
                                    onValueChange={(v) => {
                                      setEditingService({ ...editingService, unit_of_measure: v } as any);
                                      setOpenProp(null);
                                    }}
                                    options={buildUnitOptions((editingService as any).unit_of_measure, unitaOrg).map((u) => ({ value: u, label: u }))}
                                    allowCustomValue
                                    triggerClassName="h-auto px-2 py-1.5 text-[13px]"
                                  />
                                </div>
                              </PropertyRow>

                              <PropertyRow
                                label="Quantità"
                                value={qty}
                                action="cambia"
                                onClick={() => setOpenProp(openProp === 'qty' ? null : 'qty')}
                                expanded={openProp === 'qty'}
                              >
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  autoFocus
                                  className={inputCls}
                                  value={numDraft?.field === 'qty' ? numDraft.text : String(qty)}
                                  onFocus={(e) => e.target.select()}
                                  onBlur={() => setNumDraft(null)}
                                  onChange={(e) => {
                                    setNumDraft({ field: 'qty', text: e.target.value });
                                    const newQty = parseDecimalInput(e.target.value);
                                    if (newQty === null) return;
                                    setEditingService({
                                      ...editingService,
                                      quantity: newQty,
                                      ...recalcWithDiscount(+(unitPrice * newQty).toFixed(2)),
                                    } as any);
                                  }}
                                />
                              </PropertyRow>

                              {(() => {
                                // Stesso articolo su altre righe dell'offerta: le quantita' si sommano
                                const available = editingAvailability?.available;
                                if (available == null) return null;
                                const altreRighe = (selectedServices ?? []).reduce((sum, s, i) =>
                                  i !== externalSelectedServiceIndex && s.line_type === 'product' && s.product_id === editingProductId
                                    ? sum + (Number(s.quantity) || 0) : sum, 0);
                                const richiesti = altreRighe + qty;
                                if (richiesti <= available) return null;
                                const um = editingAvailability?.unit ? ` ${editingAvailability.unit}` : '';
                                return (
                                  <div role="alert" className="my-1 px-2 py-1.5 rounded text-[12px] bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                                    Oltre il disponibile: richiesti {richiesti}{um}
                                    {altreRighe > 0 ? ` (anche su altre righe)` : ''}, disponibili {available}{um}.
                                  </div>
                                );
                              })()}

                              <PropertyRow
                                label="Prezzo unitario"
                                value={money(unitPrice)}
                                aside={`· × ${qty} = ${money(+(unitPrice * qty).toFixed(2))}`}
                                action="cambia"
                                onClick={() => setOpenProp(openProp === 'unit' ? null : 'unit')}
                                expanded={openProp === 'unit'}
                              >
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  autoFocus
                                  className={inputCls}
                                  value={numDraft?.field === 'unit' ? numDraft.text : String(unitPrice)}
                                  onFocus={(e) => e.target.select()}
                                  onBlur={() => setNumDraft(null)}
                                  onChange={(e) => {
                                    setNumDraft({ field: 'unit', text: e.target.value });
                                    const newUnit = parseDecimalInput(e.target.value);
                                    if (newUnit === null) return;
                                    setEditingService({
                                      ...editingService,
                                      unit_price: newUnit,
                                      ...recalcWithDiscount(+(newUnit * qty).toFixed(2)),
                                    } as any);
                                  }}
                                />
                              </PropertyRow>

                              {/* Note di lavorazione per il magazzino: metadata.processing_note,
                                  le legge l'ordine; non vanno nel preventivo */}
                              {lineNotesEnabled && (
                                <PropertyRow
                                  label="Note di lavorazione"
                                  value={processingNoteOf(editingService.metadata) || <span className="text-black/40 dark:text-white/40">—</span>}
                                  action={processingNoteOf(editingService.metadata) ? 'cambia' : 'aggiungi'}
                                  onClick={() => setOpenProp(openProp === 'processing_note' ? null : 'processing_note')}
                                  expanded={openProp === 'processing_note'}
                                >
                                  <textarea
                                    autoFocus
                                    rows={3}
                                    className="w-full px-2 py-1.5 text-[13px] rounded border border-black/[0.12] dark:border-white/15 bg-white dark:bg-gray-800/60 text-black dark:text-white focus:border-[#2383E2] focus:outline-none focus:ring-2 focus:ring-[#2383E2]/20 transition-colors resize-y"
                                    placeholder="Indicazioni per il magazzino su questo prodotto"
                                    value={((editingService.metadata as Record<string, unknown> | undefined)?.[PROCESSING_NOTE_KEY] as string) ?? ''}
                                    onChange={(e) => setEditingService({
                                      ...editingService,
                                      metadata: withProcessingNote(editingService.metadata, e.target.value),
                                    } as any)}
                                  />
                                </PropertyRow>
                              )}

                              {/* Confezionamento: come suddividere la quantità della riga
                                  (metadata.packaging_split, lib/deals/linePackaging.ts) */}
                              {packagingSplitEnabled && (() => {
                                const split = packagingSplitOf(editingService.metadata);
                                const lineUnit = (editingService as any).unit_of_measure || orgDefaultUnit || DEFAULT_UNIT_OF_MEASURE;
                                const setSplit = (next: { packs: number; size: number } | null) => setEditingService({
                                  ...editingService,
                                  metadata: withPackagingSplit(editingService.metadata, next),
                                } as any);
                                const mismatch = split && packagingMismatch(split, qty);
                                const inputCls = 'w-20 px-2 py-1 text-[13px] rounded border border-black/[0.12] dark:border-white/15 bg-white dark:bg-gray-800/60 text-black dark:text-white focus:border-[#2383E2] focus:outline-none focus:ring-2 focus:ring-[#2383E2]/20';
                                return (
                                  <PropertyRow
                                    label="Confezionamento"
                                    value={split
                                      ? <span className={mismatch ? 'text-amber-600 dark:text-amber-400' : undefined}>{describePackagingSplit(split, lineUnit)}</span>
                                      : <span className="text-black/40 dark:text-white/40">—</span>}
                                    action={split ? 'cambia' : 'aggiungi'}
                                    onClick={() => setOpenProp(openProp === 'packaging_split' ? null : 'packaging_split')}
                                    expanded={openProp === 'packaging_split'}
                                  >
                                    <div className="space-y-1.5">
                                      <div className="flex flex-wrap items-center gap-2 text-[13px] text-black/70 dark:text-white/70">
                                        <input
                                          autoFocus
                                          type="text"
                                          inputMode="numeric"
                                          className={inputCls}
                                          placeholder="N."
                                          aria-label="Numero di confezioni"
                                          // Si conferma uscendo dal campo (o Invio): così si può scrivere liberamente
                                          key={`packs-${split?.packs ?? ''}`}
                                          defaultValue={split?.packs ?? ''}
                                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                          onBlur={(e) => {
                                            const packs = Math.round(parseDecimalInput(e.target.value) || 0);
                                            if (packs <= 0) { if (!e.target.value.trim()) setSplit(null); return; }
                                            // Dalla quantità della riga: 5 kg in 5 confezioni = 1 kg l'una
                                            setSplit({ packs, size: +(qty / packs).toFixed(4) });
                                          }}
                                        />
                                        <span>{split && split.packs === 1 ? 'confezione' : 'confezioni'} da</span>
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          className={inputCls}
                                          placeholder="Qtà"
                                          aria-label="Quantità per confezione"
                                          key={`size-${split?.size ?? ''}`}
                                          defaultValue={split ? String(split.size).replace('.', ',') : ''}
                                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                          onBlur={(e) => {
                                            const size = parseDecimalInput(e.target.value);
                                            if (size === null || size <= 0) { if (!e.target.value.trim()) setSplit(null); return; }
                                            setSplit({ packs: Math.max(1, Math.round(qty / size)), size });
                                          }}
                                        />
                                        <span>{lineUnit}</span>
                                        {split && (
                                          <button type="button" onClick={() => setSplit(null)} className="ml-auto text-[12px] text-black/50 hover:text-red-600 dark:text-white/50">
                                            Togli
                                          </button>
                                        )}
                                      </div>
                                      {mismatch && split && (
                                        <p className="text-[12px] text-amber-600 dark:text-amber-400">
                                          {describePackagingSplit(split, lineUnit)} fanno {formatQuantity(split.packs * split.size, { unit: lineUnit })}, ma la riga è di {formatQuantity(qty, { unit: lineUnit })}.
                                        </p>
                                      )}
                                    </div>
                                  </PropertyRow>
                                );
                              })()}
                            </>
                          );
                        })()}

                        {/* Servizi personalizzati: tipo e frequenza in un'unica scelta */}
                        {isCustomRow && (() => {
                          const meta: any = editingService.metadata || {};
                          const bim = meta.billing_interval_months;
                          const curVal = editingService.custom_service_type === 'one_time' ? 'ut'
                            : bim === 2 ? '2' : bim === 3 ? '3' : bim === 4 ? '4' : bim === 6 ? '6'
                            : (editingService.custom_service_type === 'annual' || bim === 12) ? '12' : '1';
                          const curInterval = curVal === 'ut' ? 0 : Number(curVal);
                          const label = curVal === 'ut' ? 'Una tantum' : BILLING_PERIOD_LABEL[Number(curVal)];
                          return (
                            <PropertyRow
                              icon={<Calendar size={12} />}
                              label="Tipo e frequenza"
                              value={label}
                              action="cambia"
                              onClick={() => setOpenProp(openProp === 'ctype' ? null : 'ctype')}
                              expanded={openProp === 'ctype'}
                            >
                              <select
                                autoFocus
                                className="px-2 py-1.5 text-[13px] rounded border border-black/[0.12] dark:border-white/15 bg-white dark:bg-gray-800/60 text-black dark:text-white focus:border-[#2383E2] focus:outline-none"
                                value={curVal}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const newMeta: any = { ...meta };
                                  let newType: 'monthly' | 'annual' | 'one_time';
                                  let newInterval = 0;
                                  if (v === 'ut') { newType = 'one_time'; delete newMeta.billing_interval_months; }
                                  else { newInterval = Number(v); newType = newInterval === 12 ? 'annual' : 'monthly'; newMeta.billing_interval_months = newInterval; }
                                  const effective = editingService.discounted_amount ?? editingService.amount ?? 0;
                                  let priceField: any = {};
                                  if (v !== 'ut' && curInterval > 0 && effective > 0 && !meta.price_override) {
                                    const perMonth = effective / curInterval;
                                    const newPrice = Math.round(perMonth * newInterval * 100) / 100;
                                    priceField = editingService.discounted_amount != null ? { discounted_amount: newPrice } : { amount: newPrice };
                                  }
                                  setEditingService({
                                    ...editingService,
                                    ...priceField,
                                    custom_service_type: newType,
                                    service: editingService.service ? { ...editingService.service, service_type: newType }
                                      : { id: editingService.service_id, title: customServiceName || 'Servizio personalizzato', description: '', service_type: newType },
                                    monthly_duration: v === 'ut' ? null : editingService.monthly_duration,
                                    metadata: newMeta,
                                  });
                                  setOpenProp(null);
                                }}
                              >
                                <option value="ut">Una tantum</option>
                                <option value="1">Mensile</option>
                                <option value="2">Bimestrale</option>
                                <option value="3">Trimestrale</option>
                                <option value="4">Quadrimestrale</option>
                                <option value="6">Semestrale</option>
                                <option value="12">Annuale</option>
                              </select>
                            </PropertyRow>
                          );
                        })()}

                        {/* Servizi a catalogo ricorrenti: frequenza con i prezzi di ogni periodo */}
                        {!isProductRow && !isCustomRow && (stype === 'monthly' || stype === 'annual') && (() => {
                          const svcRow = catalogServiceOf(editingService);
                          const rules = parseBillingPrices((svcRow as any)?.billing_prices);
                          const monthlyBase = months > 0 ? base / months : base;
                          return (
                            <PropertyRow
                              icon={<Calendar size={12} />}
                              label="Frequenza"
                              value={BILLING_PERIOD_LABEL[months] || 'Mensile'}
                              aside={(editingService.metadata as any)?.price_override ? '· prezzo bloccato a mano' : undefined}
                              action="cambia"
                              onClick={() => setOpenProp(openProp === 'freq' ? null : 'freq')}
                              expanded={openProp === 'freq'}
                            >
                              <FrequencyPicker
                                value={months}
                                onChange={applyBillingInterval}
                                monthlyBase={monthlyBase}
                                rules={rules}
                                addonsForPeriod={addonsForPeriod}
                                addonsFullForPeriod={addonsFullForPeriod}
                              />
                            </PropertyRow>
                          );
                        })()}

                        {/* Durata: resta in mesi nel database, si legge anche in anni */}
                        {(stype === 'monthly' || editingService.custom_service_type === 'monthly') && (
                          <PropertyRow
                            label={t.monthlyDuration || 'Durata'}
                            value={editingService.monthly_duration ? durationLabel(editingService.monthly_duration) : 'Illimitata'}
                            aside={editingService.monthly_duration
                              ? `· contratto ${money(rowTotalOf(editingService) * (editingService.monthly_duration / (months || 1)))}`
                              : undefined}
                            action="cambia"
                            onClick={() => setOpenProp(openProp === 'dur' ? null : 'dur')}
                            expanded={openProp === 'dur'}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                step="1"
                                autoFocus
                                className="w-[110px] px-2 py-1 text-[13px] tabular-nums rounded border border-black/[0.12] dark:border-white/15 bg-white dark:bg-gray-800/60 text-black dark:text-white focus:border-[#2383E2] focus:outline-none focus:ring-2 focus:ring-[#2383E2]/20 transition-colors"
                                value={editingService.monthly_duration || ''}
                                placeholder={t.unlimitedMonths || 'Illimitato'}
                                onChange={(e) => setEditingService({
                                  ...editingService,
                                  monthly_duration: e.target.value ? parseInt(e.target.value, 10) : null,
                                })}
                              />
                              <span className="text-[12px] text-black/45 dark:text-white/45">mesi</span>
                              {[6, 12, 24, 36].map((m) => (
                                <button
                                  key={m}
                                  type="button"
                                  onClick={() => setEditingService({ ...editingService, monthly_duration: m })}
                                  className="text-[11px] px-1.5 py-0.5 rounded border border-black/[0.1] dark:border-white/15 text-black/60 dark:text-white/60 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
                                >
                                  {m % 12 === 0 ? `${m / 12} ${m === 12 ? 'anno' : 'anni'}` : `${m} mesi`}
                                </button>
                              ))}
                            </div>
                          </PropertyRow>
                        )}

                        {/* Metodo di pagamento.
                            Una riga prodotto non ne ha uno: la fatturazione la
                            intercetta prima di guardarlo e la emette sempre come
                            rata unica al 100% alla firma (ramo line_type='product'
                            in enhancedInvoiceQueueService e nel gemello core).
                            Mostrare un selettore, per giunta in rosso quando
                            vuoto, chiedeva una scelta che nessuno avrebbe letto:
                            qui la riga dichiara solo cosa accadra' davvero. */}
                        {isProductRow ? (() => {
                          const metodoOfferta = paymentMethods.find(
                            (m: any) => m.id === (formData as any)?.payment_method_id
                          );
                          return (
                            <PropertyRow
                              icon={<FontAwesomeIcon icon={faDollarSign} className="w-3 h-3" />}
                              label={t.paymentMethod || 'Pagamento'}
                              value="Saldo unico"
                              aside={metodoOfferta
                                ? `· ${metodoOfferta.name}, per tutta l'offerta`
                                : <span className="text-amber-600 dark:text-amber-400">· metodo da scegliere</span>}
                              action={onOpenBillingSettings ? 'cambia' : null}
                              onClick={onOpenBillingSettings}
                            />
                          );
                        })() : (() => {
                          const isCustomSchedule = !editingService.payment_method_id && (editingService.payment_schedule?.installments?.length ?? 0) > 0;
                          const methodName = paymentMethods.find((m) => m.id === editingService.payment_method_id)?.name;
                          const value = isCustomSchedule
                            ? `Rate personalizzate`
                            : methodName || 'Da scegliere';
                          const visibleMethods = paymentMethods.filter((m: any) =>
                            !m.archived || m.id === editingService.payment_method_id
                          );
                          const paymentOptions = visibleMethods.map((m) => ({ value: m.id, label: m.name }));
                          const currentPaymentOption = paymentOptions.find(o => o.value === editingService.payment_method_id) || null;
                          const missing = !editingService.payment_method_id && !isCustomSchedule;
                          return (
                            <PropertyRow
                              icon={<FontAwesomeIcon icon={faDollarSign} className="w-3 h-3" />}
                              label={t.paymentMethod || 'Pagamento'}
                              value={
                                <span className={missing ? 'text-red-600 dark:text-red-400' : undefined}>{value}</span>
                              }
                              aside={isCustomSchedule
                                ? `· ${editingService.payment_schedule!.installments!.length} rate solo per questa offerta`
                                : undefined}
                              action="cambia"
                              onClick={() => setOpenProp(openProp === 'pay' ? null : 'pay')}
                              expanded={openProp === 'pay'}
                            >
                              <div className="w-[320px] max-w-full">
                                <Select
                                  autoFocus
                                  options={paymentOptions}
                                  value={currentPaymentOption}
                                  onChange={(opt: any) => {
                                    handlePaymentMethodChange(opt?.value || '');
                                    setOpenProp(null);
                                  }}
                                  placeholder={t.selectPaymentMethod || 'Cerca metodo di pagamento...'}
                                  isSearchable
                                  isClearable
                                  classNamePrefix="payment-method-select"
                                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                  menuPosition="fixed"
                                  styles={{
                                    control: (b: any, s: any) => ({
                                      ...b, minHeight: '32px', background: 'transparent',
                                      borderColor: s.isFocused ? '#2383E2' : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'),
                                      boxShadow: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                                    }),
                                    valueContainer: (b: any) => ({ ...b, padding: '2px 8px' }),
                                    indicatorSeparator: () => ({ display: 'none' }),
                                    input: (b: any) => ({ ...b, margin: 0, padding: 0, fontSize: '13px', color: isDark ? 'white' : 'black' }),
                                    placeholder: (b: any) => ({ ...b, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', fontSize: '13px' }),
                                    singleValue: (b: any) => ({ ...b, color: isDark ? 'white' : 'black', fontSize: '13px' }),
                                    menu: (b: any) => ({
                                      ...b, background: isDark ? 'rgba(30, 30, 30, 0.98)' : 'white',
                                      border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                      borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', marginTop: '4px', overflow: 'hidden',
                                    }),
                                    menuPortal: (b: any) => ({ ...b, zIndex: 99999 }),
                                    menuList: (b: any) => ({ ...b, padding: '4px' }),
                                    option: (b: any, s: any) => ({
                                      ...b,
                                      background: s.isFocused ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
                                      color: isDark ? 'white' : '#1a1a1a', padding: '6px 10px', borderRadius: '4px', fontSize: '13px', cursor: 'pointer',
                                    }),
                                  }}
                                />
                                <div className="flex items-center gap-2 mt-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const amount = rowTotalOf(editingService);
                                      const todayISO = new Date().toISOString().split('T')[0];
                                      setEditingService({
                                        ...editingService,
                                        payment_method_id: null as any,
                                        payment_schedule: {
                                          installments: [{
                                            type: 'percentage', value: 100, amount, daysAfterStart: 0,
                                            description: 'Saldo', isConfigurable: true, fixedDate: todayISO,
                                          }],
                                        },
                                      });
                                      setOpenProp(null);
                                    }}
                                    className="inline-flex items-center gap-1.5 text-[12px] font-medium text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06] px-2 py-1 rounded-md transition-colors"
                                    title="Crea rate valide solo per questa offerta"
                                  >
                                    <Settings size={12} />
                                    Rate solo per questa offerta
                                  </button>
                                  {isCustomSchedule && (
                                    <button
                                      type="button"
                                      onClick={() => setEditingService({ ...editingService, payment_schedule: { installments: [] } })}
                                      className="text-[12px] font-medium text-black/50 dark:text-white/50 hover:text-red-600 px-2 py-1 rounded-md transition-colors"
                                    >
                                      Rimuovi rate personalizzate
                                    </button>
                                  )}
                                </div>
                                {/* Le rate a giorno fisso possono partire dal mese dopo */}
                                {editingService.payment_method_id && hasFixedDayInstallments(editingService.payment_method_id) && (
                                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={useNextMonth}
                                      onChange={(e) => {
                                        setUseNextMonth(e.target.checked);
                                        if (editingService.payment_schedule?.installments) {
                                          const updated = editingService.payment_schedule.installments.map(inst => ({
                                            ...inst,
                                            nextMonth: inst.fixedDayOfMonth && e.target.checked ? true : undefined,
                                          }));
                                          setEditingService({ ...editingService, payment_schedule: { installments: updated } });
                                        }
                                      }}
                                      className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-[12px] text-black/65 dark:text-white/65">Scadenze dal prossimo mese</span>
                                  </label>
                                )}
                              </div>
                            </PropertyRow>
                          );
                        })()}

                        {/* IVA: vale per servizi e prodotti, con l'ereditarietà dichiarata.
                            Con l'IVA del cliente applicata all'offerta la riga la mostra e non si cambia. */}
                        {lockedVatRate != null ? (
                          <PropertyRow
                            label="IVA"
                            value={`${lockedVatRate}%`}
                            aside="· dal cliente"
                            action={null}
                          />
                        ) : (
                        <PropertyRow
                          label="IVA"
                          value={(editingService as any).vat_rate != null ? `${(editingService as any).vat_rate}%` : '22%'}
                          aside={(editingService as any).vat_rate == null ? '· ereditata' : undefined}
                          action="cambia"
                          onClick={() => setOpenProp(openProp === 'vat' ? null : 'vat')}
                          expanded={openProp === 'vat'}
                        >
                          <select
                            autoFocus
                            className="px-2 py-1.5 text-[13px] rounded border border-black/[0.12] dark:border-white/15 bg-white dark:bg-gray-800/60 text-black dark:text-white focus:border-[#2383E2] focus:outline-none"
                            value={(editingService as any).vat_rate ?? ''}
                            onChange={(e) => {
                              setEditingService({
                                ...editingService,
                                vat_rate: e.target.value === '' ? null : Number(e.target.value),
                              } as any);
                              setOpenProp(null);
                            }}
                          >
                            <option value="">Eredita dall'anagrafica</option>
                            {VAT_RATE_OPTIONS.map((rate) => (
                              <option key={rate} value={rate}>{rate}%</option>
                            ))}
                          </select>
                        </PropertyRow>
                        )}

                        {/* Listino e varianti: solo per le righe prodotto che ne hanno */}
                        {isProductRow && editingProductId && productPriceLists.length > 0 && (
                          <PropertyRow
                            label="Listino"
                            value={productPriceLists.find(pl => pl.id === ((editingService as any).price_list_id ?? (editingService.metadata as any)?.price_list_id))?.name || 'Prezzo standard'}
                            action="cambia"
                            onClick={() => setOpenProp(openProp === 'plist' ? null : 'plist')}
                            expanded={openProp === 'plist'}
                          >
                            <select
                              autoFocus
                              className="px-2 py-1.5 text-[13px] rounded border border-black/[0.12] dark:border-white/15 bg-white dark:bg-gray-800/60 text-black dark:text-white focus:border-[#2383E2] focus:outline-none max-w-full"
                              value={(editingService as any).price_list_id ?? (editingService.metadata as any)?.price_list_id ?? ''}
                              onChange={(e) => {
                                const listId = e.target.value || null;
                                const priceRow = listId ? productPrices.find(p => p.price_list_id === listId) : null;
                                const newPrice = priceRow ? (priceRow.sale_price ?? priceRow.regular_price ?? editingService.amount) : editingService.amount;
                                const qty = (editingService as any).quantity || 1;
                                setEditingService({
                                  ...editingService,
                                  price_list_id: listId,
                                  metadata: { ...(editingService.metadata || {}), price_list_id: listId },
                                  unit_price: newPrice,
                                  amount: newPrice * qty,
                                } as any);
                                setOpenProp(null);
                              }}
                            >
                              <option value="">Prezzo standard</option>
                              {productPriceLists.map(pl => {
                                const priceRow = productPrices.find(p => p.price_list_id === pl.id);
                                const displayPrice = priceRow?.sale_price ?? priceRow?.regular_price;
                                return (
                                  <option key={pl.id} value={pl.id}>
                                    {pl.name}{pl.is_default ? ' (default)' : ''}
                                    {displayPrice != null ? ` · €${Number(displayPrice).toFixed(2)}` : ' · n/d'}
                                  </option>
                                );
                              })}
                            </select>
                          </PropertyRow>
                        )}

                        {isProductRow && editingProductId && productVariations.length > 0 && (
                          <PropertyRow
                            label="Variante"
                            value={productVariations.find(v => v.id === ((editingService as any).variation_id ?? (editingService.metadata as any)?.variation_id))?.sku || 'Nessuna'}
                            action="cambia"
                            onClick={() => setOpenProp(openProp === 'pvar' ? null : 'pvar')}
                            expanded={openProp === 'pvar'}
                          >
                            <select
                              autoFocus
                              className="px-2 py-1.5 text-[13px] rounded border border-black/[0.12] dark:border-white/15 bg-white dark:bg-gray-800/60 text-black dark:text-white focus:border-[#2383E2] focus:outline-none max-w-full"
                              value={(editingService as any).variation_id ?? (editingService.metadata as any)?.variation_id ?? ''}
                              onChange={(e) => {
                                const varId = e.target.value || null;
                                const v = varId ? productVariations.find(x => x.id === varId) : null;
                                const newPrice = v ? (v.sale_price ?? v.regular_price ?? editingService.amount) : editingService.amount;
                                const qty = (editingService as any).quantity || 1;
                                setEditingService({
                                  ...editingService,
                                  variation_id: varId,
                                  metadata: { ...(editingService.metadata || {}), variation_id: varId, variation_sku: v?.sku || null },
                                  unit_price: newPrice,
                                  amount: newPrice * qty,
                                } as any);
                                setOpenProp(null);
                              }}
                            >
                              <option value="">Nessuna variante</option>
                              {productVariations.map(v => {
                                const price = v.sale_price ?? v.regular_price;
                                const labelParts = [
                                  v.sku || 'Variante',
                                  price != null ? `€${Number(price).toFixed(2)}` : null,
                                  v.stock_quantity != null ? `${v.stock_quantity} disp.` : null,
                                ].filter(Boolean);
                                return <option key={v.id} value={v.id}>{labelParts.join(' · ')}</option>;
                              })}
                            </select>
                          </PropertyRow>
                        )}
                      </div>
                      {/* === FINE PROPRIETÀ === */}
                    </>
                  );
                })()}

                {/* === ADD-ON === */}
                {(
                  (editingService.line_type === 'product' && editingService.product_id) ||
                  (editingService.line_type !== 'product' && !editingService.service_id.startsWith('custom-'))
                ) && (() => {
                  const selected = editingService.addons ?? [];
                  const billable = billableAddonsTotal(editingService);
                  return (
                    <PanelGroup
                      title="Add-on"
                      meta={selected.length ? `${selected.length} ${selected.length === 1 ? 'scelto' : 'scelti'}` : 'nessuno'}
                      right={
                        billable > 0 ? (
                          <span className="text-[12px] font-medium text-black/55 dark:text-white/55 tabular-nums">
                            + {money(billable)}
                          </span>
                        ) : undefined
                      }
                      defaultOpen={selected.length > 0}
                    >
                      <DealServiceAddonsPicker
                        // Header e totale li dà il gruppo del pannello: qui
                        // servono solo le righe degli add-on.
                        chrome={false}
                        serviceId={editingService.line_type === 'product' ? undefined : editingService.service_id}
                        productId={editingService.line_type === 'product' ? (editingService.product_id ?? undefined) : undefined}
                        selectedAddons={selected}
                        // Base = importo riga MENO gli add-on già sommati dentro amount
                        // (l'amount della riga include gli add-on billable).
                        baseAmount={baseAmountOf(editingService)}
                        // Il prezzo a catalogo è del periodo base: su una riga
                        // annuale gli add-on vanno mostrati e salvati sull'anno.
                        billingMonths={billingMonthsOf(editingService)}
                        serviceType={serviceTypeOf(editingService)}
                        onChange={(nextAddons) => {
                          // Scostamento del totale add-on rispetto allo stato precedente:
                          // amount = base + add-on non inclusi nel prezzo base.
                          const sumBillable = (arr: typeof nextAddons) =>
                            arr
                              .filter((a) => !a.is_included_in_base)
                              .reduce((s, a) => s + (Number.isFinite(a.amount) ? a.amount : 0), 0);
                          const delta = sumBillable(nextAddons) - sumBillable(editingService.addons ?? []);
                          setEditingService({
                            ...editingService,
                            addons: nextAddons,
                            amount: Math.max(0, (editingService.amount ?? 0) + delta),
                            discounted_amount:
                              editingService.discounted_amount != null
                                ? Math.max(0, editingService.discounted_amount + delta)
                                : editingService.discounted_amount,
                          });
                        }}
                      />
                    </PanelGroup>
                  );
                })()}

                {/* === RATE E SCADENZE === */}
                {(() => {
                  const installments = editingService.payment_schedule?.installments ?? [];
                  if (!installments.length) return null;
                  const total = rowTotalOf(editingService);
                  const sum = installments.reduce((s, inst) => {
                    const v = inst.type === 'percentage' ? (total * (Number(inst.value) || 0)) / 100 : Number(inst.value) || 0;
                    return s + v;
                  }, 0);
                  const balanced = Math.abs(sum - total) < 0.01;
                  return (
                    <PanelGroup
                      title="Rate e scadenze"
                      meta={`${installments.length} ${installments.length === 1 ? 'rata' : 'rate'} · ${installments
                        .map((i) => (i.type === 'percentage' ? `${i.value}%` : money(Number(i.value) || 0)))
                        .join(' + ')}`}
                      right={
                        !balanced ? (
                          <span className="text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400">
                            non quadra
                          </span>
                        ) : undefined
                      }
                      // Aperto quando il metodo prevede rate da configurare o
                      // quando la somma non torna: prima si apriva da solo un
                      // drawer sopra il pannello.
                      open={ratesOpen || !balanced}
                      onOpenChange={setRatesOpen}
                    >
                      <RateScheduleInline
                        installments={installments}
                        total={total}
                        onChange={(next) => setEditingService({
                          ...editingService,
                          payment_schedule: { installments: next },
                        })}
                        onOpenFull={() => setSheet('rates')}
                      />
                    </PanelGroup>
                  );
                })()}

                {/* === DESCRIZIONE PER IL PREVENTIVO === */}
                {(() => {
                  const isProductRow = editingService.line_type === 'product';
                  const summaryName = isProductRow
                    ? ((editingService as any).custom_service_name?.trim()
                        || products.find(pp => pp.id === editingService.product_id)?.name
                        || 'Prodotto')
                    : editingService.service_id.startsWith('custom-')
                      ? (customServiceName || (t.customService || 'Servizio personalizzato'))
                      : (services.find(s => s.id === editingService.service_id)?.title || (t.service || 'Servizio'));
                  const price = editingService.discounted_amount ?? editingService.amount;
                  const priceLabel = Number.isFinite(price)
                    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price as number)
                    : null;
                  const summary = (
                    <span className="inline-flex items-center gap-2 max-w-full">
                      <span className="font-medium text-black/70 dark:text-white/70 truncate">{summaryName}</span>
                      {priceLabel && (
                        <>
                          <span className="opacity-40">·</span>
                          <span className="tabular-nums">{priceLabel}</span>
                        </>
                      )}
                    </span>
                  );
                  const filled = !!editingService.description?.trim();
                  return (
                    <PanelGroup
                      title={isProductRow ? 'Descrizione' : (t.serviceDescription || 'Descrizione per il preventivo')}
                      meta={filled ? 'personalizzata' : 'vuota'}
                      open={descOpen}
                      onOpenChange={setDescOpen}
                      right={
                        <span
                          role="button"
                          tabIndex={0}
                          // Stessa istanza TipTap dell'editor inline: il foglio
                          // lo apre il DealServiceEditor, così cursore e
                          // cronologia non si perdono.
                          onClick={(e) => { e.stopPropagation(); openDescriptionSheet(); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openDescriptionSheet(); } }}
                          className="text-[11px] font-medium text-[#2383E2] hover:underline"
                        >
                          apri in grande
                        </span>
                      }
                      defaultOpen={filled}
                    >
                      <DealServiceEditor
                        ref={descEditorRef}
                        content={editingService.description || ''}
                        onChange={(content) => setEditingService({ ...editingService, description: content })}
                        placeholder={t.serviceDescriptionPlaceholder || 'Descrizione dettagliata del servizio...'}
                        translations={t.ai}
                        serviceTitle={summaryName}
                        summary={summary}
                      />
                    </PanelGroup>
                  );
                })()}

                {/* === DESCRIZIONE IN FATTURA ===
                    Quando valorizzata sostituisce "{titolo servizio} - Rata X" su FIC. */}
                <PanelGroup
                  title="Descrizione in fattura"
                  meta={(editingService as any).invoice_description?.trim() ? 'personalizzata' : 'automatica'}
                  defaultOpen={!!(editingService as any).invoice_description?.trim()}
                >
                  <p className="text-[11px] text-black/45 dark:text-white/45 mb-1.5">
                    Lasciando vuoto: «{(services.find(s => s.id === editingService.service_id)?.title || 'Servizio')} — rata 1 di N».
                    Qui niente formattazione: in fattura il testo va in un campo semplice.
                  </p>
                  <textarea
                    value={(editingService as any).invoice_description || ''}
                    onChange={(e) => setEditingService({
                      ...editingService,
                      invoice_description: e.target.value || null,
                    } as any)}
                    placeholder="Testo personalizzato per la fattura…"
                    rows={3}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-black/[0.08] dark:border-white/10 bg-transparent text-black dark:text-white placeholder:text-black/35 dark:placeholder:text-white/35 focus:outline-none focus:border-[#E91E8C]/40 transition-colors resize-y"
                  />
                </PanelGroup>
                </div>
              )}

              {activeTab === 'variables' && (
                <div className="space-y-5 py-4">
                  {/* Cost Preview - Only for non-custom services */}
                  {!editingService.service_id.startsWith('custom-') && editingService.service_id && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-[#E91E8C] mb-2">
                        Costi del servizio
                      </div>
                      <DealServiceCostPreview
                        serviceId={editingService.service_id}
                        serviceName={editingService.service?.title || 'Servizio'}
                      />
                    </div>
                  )}

                  {/* Service Variables Section */}
                  <div>
                    <DealVariablesSection
                      variables={serviceVariables}
                      onVariablesChange={handleVariablesChange}
                      dealServiceId={editingService?.id}
                      readonly={false}
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Totale della riga: prima viveva dentro il riquadro add-on e
                spariva chiudendolo. Qui resta sempre in vista, col dettaglio
                base + add-on accanto. */}
            {activeTab === 'details' && (() => {
              const base = baseAmountOf(editingService);
              const addons = billableAddonsTotal(editingService);
              const total = rowTotalOf(editingService);
              const stype = serviceTypeOf(editingService);
              const recurring = editingService.line_type !== 'product' && !!stype && stype !== 'one_time';
              const suffix = recurring ? BILLING_PERIOD_SUFFIX[billingMonthsOf(editingService)] || '/mese' : '';
              return (
                <div className="flex-shrink-0 flex items-baseline gap-2.5 px-5 py-2.5 border-t border-black/[0.06] dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] tabular-nums">
                  <span className="text-[11px] uppercase tracking-wide font-medium text-black/45 dark:text-white/45">
                    Totale riga
                  </span>
                  {addons > 0 && (() => {
                    // Con base 0 lo sconto della riga cade sugli add-on: dire
                    // "base 0 + add-on 1.562" accanto a un totale di 1.406
                    // sembrerebbe un errore di somma.
                    const discount = Math.round((base + addons - total) * 100) / 100;
                    return (
                      <span className="text-[12px] text-black/40 dark:text-white/40">
                        base {money(base)} + add-on {money(addons)}
                        {discount > 0.005 && <> − sconto {money(discount)}</>}
                      </span>
                    );
                  })()}
                  <span className="ml-auto text-[16px] font-semibold tracking-tight text-black dark:text-white">
                    {money(total)}
                    {suffix && (
                      <span className="text-[12px] font-medium text-black/50 dark:text-white/50">{` ${suffix}`}</span>
                    )}
                  </span>
                </div>
              );
            })()}

            {/* Footer rimosso: l'auto-save propaga `editingService` nel
                carrello tramite un useEffect debounced (vedi sopra). La
                persistenza a DB resta delegata al bottone "Salva offerta"
                in alto. Il Rimuovi e' migrato nell'header per liberare
                spazio in basso. */}
          </div>
        )}
        
      </div>
      
      {/* Payment Schedule Calculator — Drawer da destra, Notion-like.
          Slide-in da right, full-height, 50vw (max 760px). Backdrop sottile
          per non distrarre dall'editor principale dietro. Esc per chiudere. */}
      {/* Rate a tutta pagina: stesso foglio centrale della descrizione, con
          dentro il calcolatore completo. Le righe inline del pannello bastano
          per correggere una rata; qui si lavora un piano lungo. */}
      {editingService && (
        <FullPageSheet
          open={sheet === 'rates'}
          onClose={() => setSheet(null)}
          kind="Rate e scadenze"
          subtitle={services.find(s => s.id === editingService.service_id)?.title
            || editingService.custom_service_name
            || 'Riga dell\'offerta'}
          note="La somma delle rate deve corrispondere al totale della riga, add-on compresi."
        >
          <div className="max-w-[720px] mx-auto px-6 py-6">
            <PaymentScheduleCalculator
              serviceAmount={rowTotalOf(editingService)}
              serviceType={serviceTypeOf(editingService) as any}
              billingMonths={billingMonthsOf(editingService)}
              referenceDate={dealSignedAt}
              existingInstallments={(() => {
                const ps = editingService.payment_schedule as { installments?: DealPaymentInstallment[] } | DealPaymentInstallment[] | undefined;
                return Array.isArray(ps) ? ps : ps?.installments || [];
              })()}
              onSave={(installments) => {
                handleSaveSchedule(installments);
                setSheet(null);
              }}
              onClose={() => setSheet(null)}
            />
          </div>
        </FullPageSheet>
      )}

    </div>
  );
}